/**
 * French message catalogue. The source of truth for the message key set:
 * every other locale is typed against `MessageKey` derived from this object,
 * so a missing translation is a compile error, not a silent fallback.
 *
 * `common.*` through `stepper.*`: the original ui-kit-primitive scope
 * (Field, Input, PhoneInput, GovernorateSelect, Pagination, Stepper).
 *
 * `storefront.*`: added for the "Élimination BIENTÔT" i18n sprint's
 * Phase 2 — every key's French value is the EXACT existing wording
 * already shipped across `apps/storefront` (never reworded), just
 * relocated here so it can be looked up by locale. Server-relayed error
 * messages (the real API's own SPECIFIC business strings — coupon
 * rejection, checkout validation, order-lookup) are deliberately NOT
 * catalogued: the backend itself has exactly one locale in production use
 * today, so translating only the client-side half of an error surface
 * would show a French sentence and a translated one side by side
 * depending on which failed — more confusing than leaving that whole
 * surface untranslated, documented as a known limitation rather than
 * solved partially.
 *
 * `errors.*`: the GENERIC/framework error surface is a different problem
 * with a different fix — see `../errors.ts`. The API now attaches a
 * stable `errorCode` (never a free-text message) to every generic failure
 * (network-adjacent, auth, permission, not-found, validation, conflict,
 * rate-limit, server), and `resolveErrorMessageKey()` maps that code to
 * one of the keys below — fully translated, every time, never a mix of
 * one translated sentence and one raw API string. This does not
 * contradict the note above: `errors.*` never carries a SPECIFIC business
 * message (still out of scope), only the generic "something went wrong,
 * here is the shape of it" surface named explicitly for this sprint.
 */
export const fr = {
  'common.loading': 'Chargement…',
  'common.close': 'Fermer',
  'common.dismiss': 'Ignorer',
  'common.retry': 'Réessayer',
  'common.required': 'Obligatoire',
  'common.optional': 'Facultatif',
  'common.tempPassword.title': 'Mot de passe provisoire',
  'common.tempPassword.once':
    "Ce mot de passe ne sera affiché qu'une seule fois. Notez-le maintenant et transmettez-le par un canal sûr.",
  'common.tempPassword.account': 'Compte',
  'common.tempPassword.label': 'Mot de passe provisoire',
  'common.tempPassword.show': 'Afficher',
  'common.tempPassword.hide': 'Masquer',
  'common.tempPassword.copy': 'Copier',
  'common.tempPassword.copied': 'Mot de passe copié',
  'common.tempPassword.copyFailed': 'Copie impossible : notez le mot de passe à la main.',
  'common.tempPassword.expires':
    "Valable jusqu'au {{date}}. Passé ce délai, il faudra en générer un nouveau.",
  'common.tempPassword.mustChange':
    'La personne devra choisir son propre mot de passe dès sa première connexion.',
  'common.tempPassword.clipboard':
    'Votre presse-papiers peut garder une copie de ce mot de passe : videz-le après usage.',
  'common.tempPassword.done': "J'ai noté le mot de passe",
  'common.tempPassword.gone':
    "Le mot de passe n'est plus affiché. S'il n'a pas été noté, générez-en un nouveau.",
  'common.changePassword.current': 'Mot de passe actuel',
  'common.changePassword.new': 'Nouveau mot de passe',
  'common.changePassword.confirm': 'Confirmer le nouveau mot de passe',
  'common.changePassword.rules': 'Au moins 12 caractères, différent du mot de passe actuel.',
  'common.changePassword.submit': 'Enregistrer mon mot de passe',
  'common.changePassword.currentRequired': 'Saisissez votre mot de passe actuel.',
  'common.changePassword.tooShort': 'Le nouveau mot de passe doit contenir au moins 12 caractères.',
  'common.changePassword.mismatch': 'Les deux mots de passe ne sont pas identiques.',
  'common.changePassword.same': 'Choisissez un mot de passe différent du mot de passe actuel.',
  'common.selectPlaceholder': 'Sélectionner…',

  'validation.required': 'Ce champ est obligatoire.',
  'validation.phoneInvalid': 'Numéro de téléphone tunisien invalide.',
  'validation.governorateRequired': 'Veuillez sélectionner un gouvernorat.',
  'validation.delegationRequired': 'Veuillez sélectionner une délégation.',

  'phone.countryCode': '+216',
  'phone.placeholder': '22 345 678',

  'governorate.label': 'Gouvernorat',
  'delegation.label': 'Délégation',

  'pagination.label': 'Pagination',
  'pagination.previous': 'Page précédente',
  'pagination.next': 'Page suivante',
  'pagination.page': 'Page',

  'stepper.label': 'Progression',
  'stepper.completed': 'Étape terminée',
  'stepper.current': 'Étape en cours',
  'stepper.upcoming': 'Étape à venir',

  // ---- storefront: navigation (header, shared across pages) ----
  'storefront.nav.shop': 'Boutique',
  'storefront.nav.categories': 'Catégories',
  'storefront.nav.products': 'Produits',
  'storefront.nav.orderTracking': 'Suivi de commande',
  'storefront.nav.cart': 'Panier',
  'storefront.nav.cartWithCount': 'Panier — {{count}} article{{plural}}',
  'storefront.nav.primary': 'Navigation principale',

  // ---- storefront: footer ----
  'storefront.footer.contact': 'Coordonnées',
  'storefront.footer.hours': 'Horaires',
  'storefront.footer.policies': 'Politiques',
  'storefront.footer.shipping': 'Livraison',
  'storefront.footer.returns': 'Retours',
  'storefront.footer.terms': 'Conditions générales',
  'storefront.footer.privacy': 'Confidentialité',
  'storefront.footer.usefulLinks': 'Liens utiles',
  'storefront.footer.buyWithConfidence': 'Acheter en confiance',
  'storefront.footer.cod': 'Paiement à la livraison',
  'storefront.footer.deliveryNational': 'Livraison dans les 24 gouvernorats',
  'storefront.footer.deliveryFee': 'Frais de livraison : {{fee}}',
  'storefront.footer.noDeliveryFee': 'Aucun frais de livraison',
  'storefront.footer.whatsappCta': 'Écrire sur WhatsApp',
  'storefront.footer.call': 'Appeler',
  'storefront.footer.poweredBy': 'Propulsé par Souk',
  'storefront.footer.rights': '© {{year}} {{name}}',
  'storefront.footer.day.monday': 'Lundi',
  'storefront.footer.day.tuesday': 'Mardi',
  'storefront.footer.day.wednesday': 'Mercredi',
  'storefront.footer.day.thursday': 'Jeudi',
  'storefront.footer.day.friday': 'Vendredi',
  'storefront.footer.day.saturday': 'Samedi',
  'storefront.footer.day.sunday': 'Dimanche',

  // ---- storefront: home ----
  'storefront.home.ctaProducts': 'Découvrir les produits',
  'storefront.home.ctaCategories': 'Parcourir les catégories',
  'storefront.collections.title': 'Nos collections',
  'storefront.collections.count': '{{count}} produit{{plural}}',
  'storefront.collections.all': 'Toutes les collections',
  'storefront.story.eyebrow': 'À propos',
  'storefront.story.defaultTitle': 'Notre histoire',
  'storefront.story.cta': 'Découvrir nos produits',
  'storefront.featured.eyebrow': 'La sélection de la boutique',
  'storefront.featured.title': 'Nos coups de cœur',
  'storefront.featured.subtitle': 'Les produits que {{shopName}} vous recommande.',
  'storefront.featured.badge': 'Coup de cœur',
  'storefront.featured.cta': 'Découvrir',
  'storefront.newArrivals.eyebrow': "Vient d'arriver",
  'storefront.newArrivals.title': 'Nouveautés',
  'storefront.newArrivals.badge': 'Nouveau',
  'storefront.newArrivals.prev': 'Précédent',
  'storefront.newArrivals.next': 'Suivant',
  'storefront.home.lowStockTitle': 'Stock limité',
  'storefront.home.lowStockBadge': 'Derniers articles',
  'storefront.home.productsTitle': 'Nos produits',
  'storefront.home.viewAll': 'Voir tout',
  'storefront.home.emptyProducts': "Cette boutique n'a pas encore publié de produit.",

  // ---- storefront: reassurance bar ----
  'storefront.reassurance.codTitle': 'Paiement à la livraison',
  'storefront.reassurance.codDescription':
    'Réglez en espèces à la réception de votre colis, aucun paiement en ligne.',
  'storefront.reassurance.deliveryTitle': 'Livraison nationale',
  'storefront.reassurance.returnsTitle': 'Retours',
  'storefront.reassurance.returnsDescription':
    'La boutique a publié sa politique de retour : consultez-la avant de commander.',
  'storefront.trust.deliveryWithFee': 'dans les 24 gouvernorats. Frais de livraison : {{fee}}.',
  'storefront.trust.deliveryNoFee': 'dans les 24 gouvernorats, sans frais de livraison.',
  'storefront.trust.deliveryStripWithFee': 'Dans les 24 gouvernorats · frais : {{fee}}',
  'storefront.trust.deliveryStripNoFee': 'Dans les 24 gouvernorats · sans frais',
  'storefront.trust.returnsSummary': 'Politique de retour de la boutique',
  'storefront.trust.contactTitle': 'Une question ? Écrivez ou appelez la boutique.',
  'storefront.trust.email': 'Envoyer un email',
  'storefront.common.noFee': 'Aucun frais',
  'storefront.reassurance.supportTitle': 'Support client',

  // ---- storefront: categories page ----
  'storefront.categories.title': 'Catégories',
  'storefront.categories.empty': "Cette boutique n'a pas encore publié de catégorie.",
  'storefront.categories.count': '{{count}} collection{{plural}}',
  'storefront.categories.discover': 'Découvrir',
  'storefront.categories.moreLabel': '{{count}} autres sous-catégories de {{name}}',

  // ---- storefront: products (catalogue) page ----
  'storefront.products.title': 'Produits',
  'storefront.products.viewAll': 'Voir tous les produits',
  'storefront.products.emptyCategory': 'Aucun produit publié dans cette catégorie pour le moment.',
  'storefront.products.emptyAll': "Cette boutique n'a pas encore publié de produit.",
  'storefront.catalog.allProducts': 'Tous les produits',
  'storefront.catalog.chipAll': 'Tout',
  'storefront.catalog.chipsLabel': 'Catégories',
  'storefront.catalog.count': '{{count}} produit{{plural}}',
  'storefront.catalog.filter': 'Filtrer',
  'storefront.catalog.filterWithCount': 'Filtrer · {{count}}',
  'storefront.catalog.sortLabel': 'Trier par',
  'storefront.catalog.sort.featured': 'Sélection',
  'storefront.catalog.sort.newest': 'Nouveautés',
  'storefront.catalog.sort.priceAsc': 'Prix croissant',
  'storefront.catalog.sort.priceDesc': 'Prix décroissant',
  'storefront.catalog.sort.nameAsc': 'Nom A–Z',
  'storefront.catalog.filters.title': 'Filtres',
  'storefront.catalog.filters.close': 'Fermer',
  'storefront.catalog.filters.price': 'Prix',
  'storefront.catalog.filters.priceMin': 'Min',
  'storefront.catalog.filters.priceMax': 'Max',
  'storefront.catalog.filters.currency': 'DT',
  'storefront.catalog.filters.availability': 'Disponibilité',
  'storefront.catalog.filters.inStock': 'En stock uniquement',
  'storefront.catalog.filters.promotions': 'Promotions',
  'storefront.catalog.filters.onSale': 'Produits en promotion',
  'storefront.catalog.filters.brand': 'Marque',
  'storefront.catalog.filters.clear': 'Effacer',
  'storefront.catalog.filters.apply': 'Voir {{count}} résultat{{plural}}',
  'storefront.catalog.active.groupLabel': 'Filtres actifs',
  'storefront.catalog.active.priceRange': '{{min}} – {{max}} DT',
  'storefront.catalog.active.priceFrom': 'Dès {{min}} DT',
  'storefront.catalog.active.priceUpTo': "Jusqu'à {{max}} DT",
  'storefront.catalog.active.inStock': 'En stock',
  'storefront.catalog.active.onSale': 'En promotion',
  'storefront.catalog.active.clearAll': 'Tout effacer',
  'storefront.catalog.active.remove': 'Retirer le filtre : {{label}}',
  'storefront.catalog.showMore': 'Afficher {{count}} de plus',
  'storefront.catalog.progress': 'Vous avez vu {{shown}} produits sur {{total}}',
  'storefront.catalog.emptyFiltered': 'Aucun produit ne correspond à ces filtres.',
  'storefront.catalog.clearFilters': 'Effacer les filtres',
  'storefront.card.add': 'Ajouter',
  'storefront.card.chooseOptions': 'Choisir les options',
  'storefront.card.unavailable': 'Indisponible',
  'storefront.card.soldOut': 'Rupture de stock',
  'storefront.card.lowStockLine': 'Plus que {{count}} en stock',
  'storefront.wishlist.add': 'Ajouter aux favoris',
  'storefront.wishlist.remove': 'Retirer des favoris',
  'storefront.wishlist.navLabel': 'Mes favoris',
  'storefront.wishlist.navLabelWithCount': 'Mes favoris — {{count}}',
  'storefront.wishlist.title': 'Mes favoris',
  'storefront.wishlist.deviceNote': 'Favoris enregistrés sur cet appareil',
  'storefront.wishlist.deviceNoteSub': 'Ils ne sont pas synchronisés avec un compte.',
  'storefront.wishlist.empty': "Vous n'avez pas encore de favoris.",
  'storefront.wishlist.emptyHint': "Touchez le cœur d'un produit pour le retrouver ici.",
  'storefront.wishlist.emptyCta': 'Voir le catalogue',
  'storefront.wishlist.loadError': 'Impossible de charger vos favoris pour le moment.',

  // ---- storefront: product detail ----
  'storefront.product.similar': 'Vous aimerez aussi',
  'storefront.product.viewPhoto': 'Voir la photo {{n}}',
  'storefront.product.addToCart': 'Ajouter au panier',
  'storefront.product.addShort': 'Ajouter',
  'storefront.product.adding': 'Ajout…',
  'storefront.product.addedToCart': 'Ajouté au panier.',
  'storefront.product.quickAddLabel': 'Ajout rapide au panier',
  'storefront.product.quickAddedLabel': 'Ajouté au panier',
  'storefront.product.notFoundTitle': 'Produit introuvable',
  'storefront.product.notFoundDescription': "Ce produit n'existe pas ou n'est plus disponible.",
  'storefront.product.selectOptionsPrompt': 'Choisissez une option',
  'storefront.product.combinationUnavailable': "Cette combinaison n'est pas disponible.",

  // ---- storefront: stock states (mirrors StockStateBadge's own merchant-side vocabulary) ----
  'storefront.stock.inStock': 'En stock',
  'storefront.stock.inStockCount': 'En stock · {{count}}',
  'storefront.stock.lowStock': 'Stock limité',
  'storefront.stock.outOfStock': 'Rupture de stock',
  'storefront.stock.unavailable': 'Indisponible',
  'storefront.stock.badgeOutOfStock': 'Rupture',
  'storefront.stock.badgeLowStock': 'Plus que {{count}}',

  // ---- storefront: shared across cart / checkout / order lookup ----
  'storefront.common.subtotal': 'Sous-total',
  'storefront.common.discount': 'Remise',
  'storefront.common.deliveryFee': 'Livraison',
  'storefront.common.total': 'Total',
  'storefront.common.codReassurance': 'Paiement à la livraison — vous ne payez qu’à la réception.',
  'storefront.common.backToShop': 'Retourner à la boutique',
  'storefront.common.emptyCart': 'Votre panier est vide.',

  // ---- storefront: cart page ----
  'storefront.cart.title': 'Panier',
  'storefront.cart.remove': 'Retirer',
  'storefront.cart.removedMessage': '{{name}} retiré du panier.',
  'storefront.cart.couponPlaceholder': 'Code promo',
  'storefront.cart.couponLabel': 'Code promo',
  'storefront.cart.couponApply': 'Appliquer',
  'storefront.cart.couponApplied': 'Code « {{code}} » appliqué.',
  'storefront.cart.couponInvalid': 'Le code « {{code}} » n’est plus valide et a été retiré.',
  'storefront.cart.checkoutCta': 'Passer la commande',
  'storefront.cart.continueShopping': '← Continuer mes achats',

  // ---- storefront: checkout page ----
  'storefront.checkout.title': 'Commande',
  'storefront.checkout.stepContact': 'Coordonnées',
  'storefront.checkout.stepDelivery': 'Livraison',
  'storefront.checkout.stepSummary': 'Récapitulatif',
  'storefront.checkout.submitPending': 'Validation en cours…',
  'storefront.checkout.submit': 'Confirmer la commande',
  'storefront.checkout.sectionContact': 'Coordonnées',
  'storefront.checkout.fieldName': 'Nom complet',
  'storefront.checkout.fieldPhone': 'Téléphone',
  'storefront.checkout.fieldEmail': 'Email (optionnel)',
  'storefront.checkout.sectionDelivery': 'Livraison',
  'storefront.checkout.fieldGovernorate': 'Gouvernorat',
  'storefront.checkout.fieldGovernoratePlaceholder': 'Choisir…',
  'storefront.checkout.fieldDelegation': 'Délégation',
  'storefront.checkout.fieldAddress': 'Adresse',
  'storefront.checkout.fieldAddressExtra': "Complément d'adresse (optionnel)",
  'storefront.checkout.fieldNotes': 'Notes (optionnel)',
  'storefront.checkout.summaryTitle': 'Récapitulatif',
  'storefront.checkout.editCart': 'Modifier le panier',
  'storefront.checkout.codBadge': 'Populaire',
  'storefront.checkout.codLabel': 'Paiement à la livraison',
  'storefront.checkout.noShippingFee': 'Livraison sans frais pour cette boutique.',
  'storefront.checkout.successTitle': 'Commande confirmée !',
  'storefront.checkout.successPrefix': 'Votre commande',
  'storefront.checkout.successSuffix': 'a bien été enregistrée.',
  'storefront.checkout.successTotal': 'Total à régler',
  'storefront.checkout.successCod':
    'Paiement à la livraison — vous ne payez qu’à la réception de votre colis.',

  // ---- storefront: order lookup ----
  'storefront.checkout.errors.nameRequired': 'Le nom est requis.',
  'storefront.checkout.errors.phoneInvalid':
    'Numéro de téléphone invalide : 8 chiffres, avec ou sans +216, ne commençant ni par 0 ni par 1.',
  'storefront.checkout.errors.governorateRequired': 'Le gouvernorat est requis.',
  'storefront.checkout.errors.delegationRequired': 'La délégation est requise.',
  'storefront.checkout.errors.addressRequired': "L'adresse est requise.",
  'storefront.orderLookup.errors.orderNumberInvalid':
    'Numéro de commande invalide : chiffres uniquement.',
  'storefront.orderLookup.title': 'Suivi de commande',
  'storefront.orderLookup.subtitle':
    "Retrouvez votre commande avec son numéro et le numéro de téléphone utilisé lors de l'achat.",
  'storefront.orderLookup.orderNumberLabel': 'Numéro de commande',
  'storefront.orderLookup.phoneLabel': 'Téléphone utilisé lors de la commande',
  'storefront.orderLookup.submit': 'Rechercher',
  'storefront.orderLookup.submitPending': 'Recherche en cours…',
  'storefront.orderLookup.resultTitle': 'Commande',
  'storefront.orderLookup.deliveryTitle': 'Livraison',
  'storefront.orderLookup.trackingTitle': 'Suivi',
  'storefront.orderLookup.parcel.title': 'Votre colis',
  'storefront.orderLookup.parcel.tracking': 'N° de suivi',
  'storefront.orderLookup.parcel.updated': 'Mis à jour le',
  'storefront.orderLookup.parcel.status.registered': 'Colis enregistré, en attente d’enlèvement',
  'storefront.orderLookup.parcel.status.pickup_scheduled': 'Enlèvement programmé',
  'storefront.orderLookup.parcel.status.in_transit': 'En cours d’acheminement',
  'storefront.orderLookup.parcel.status.out_for_delivery': 'En cours de livraison',
  'storefront.orderLookup.parcel.status.delivered': 'Livré',
  'storefront.orderLookup.parcel.status.return_in_progress':
    'Livraison impossible — retour en cours',
  'storefront.orderLookup.parcel.status.returned': 'Colis retourné à la boutique',
  'storefront.orderLookup.parcel.status.checking':
    'Nous vérifions votre colis — la boutique vous contactera',
  'storefront.orderLookup.parcel.status.cancelled': 'Expédition annulée',
  'storefront.orderLookup.parcel.status.other': 'Suivi en cours',

  // ---- storefront: order status vocabulary (matches OrderStatusBadge's merchant-side labels exactly) ----
  'storefront.orderStatus.ORDER_CREATED': 'Créée',
  'storefront.orderStatus.PAYMENT_PENDING': 'Paiement en attente',
  'storefront.orderStatus.PAYMENT_CONFIRMED': 'Paiement à la livraison',
  'storefront.orderStatus.PAYMENT_FAILED': 'Paiement échoué',
  'storefront.orderStatus.PROCESSING': 'En préparation',
  'storefront.orderStatus.PACKED': 'En livraison',
  'storefront.orderStatus.CANCELLED': 'Annulée',
  'storefront.orderStatus.SHIPPED': 'En livraison',
  'storefront.orderStatus.DELIVERY_FAILED': 'Échec de livraison',
  'storefront.orderStatus.DELIVERED': 'Livrée',
  'storefront.orderStatus.RETURN_REQUESTED': 'Retour demandé',
  'storefront.orderStatus.RETURNED': 'Retournée',
  'storefront.orderStatus.REFUNDED': 'Remboursée',
  'storefront.orderStatus.PARTIALLY_REFUNDED': 'Partiellement remboursée',

  // ---- storefront: not-found boundaries ----
  'storefront.notFound.shopTitle': 'Boutique introuvable',
  'storefront.notFound.shopDescription':
    "Cette boutique n'existe pas ou n'est pas accessible pour le moment.",

  // ---- storefront: platform landing ----
  'storefront.landing.title': 'Souk',
  'storefront.landing.description':
    'Chaque boutique Souk dispose de sa propre adresse publique. Rendez-vous directement sur le lien de la boutique que vous cherchez pour consulter son catalogue.',

  // ---- merchant: shared layout (sidebar, topbar, command palette, shop switcher) ----
  'merchant.sidebar.brand': 'Souk',
  'merchant.sidebar.subtitle': 'Espace marchand',
  'merchant.sidebar.navLabel': 'Navigation principale',

  'merchant.nav.group.principal': 'Principal',
  'merchant.nav.group.operations': 'Opérations',
  'merchant.nav.group.configuration': 'Configuration',
  'merchant.nav.dashboard': 'Tableau de bord',
  'merchant.nav.orders': 'Commandes',
  'merchant.nav.products': 'Produits & stock',
  'merchant.nav.inventory': 'Inventaire',
  'merchant.nav.customers': 'Clients',
  'merchant.nav.marketing': 'Marketing',
  'merchant.nav.analytics': 'Analytics',
  'merchant.nav.delivery': 'Livraison',
  'merchant.nav.deliveryAccount': 'Compte Jibex',
  'merchant.nav.payments': 'Paiements & COD',
  'merchant.nav.invoices': 'Factures',
  'merchant.nav.store': 'Boutique',
  'merchant.nav.categories': 'Catégories',
  'merchant.nav.promotions': 'Promotions',
  'merchant.nav.team': 'Équipe & permissions',
  'merchant.nav.settings': 'Paramètres',

  'merchant.topbar.searchPlaceholder': 'Rechercher, aller à…',
  'merchant.topbar.themeToggleAria': 'Changer de thème',
  'merchant.topbar.lightMode': 'Mode clair',
  'merchant.topbar.darkMode': 'Mode sombre',
  'merchant.topbar.logout': 'Se déconnecter',
  'merchant.topbar.menu': 'Ouvrir le menu',
  'merchant.topbar.more': 'Plus d’options',

  'merchant.shopSwitcher.none': 'Aucune boutique',
  'merchant.shopSwitcher.unnamed': 'Boutique sans nom',
  'merchant.shopSwitcher.activeAria': 'Boutique active',
  'merchant.shopSwitcher.suspended': 'suspendu',

  'merchant.commandPalette.label': 'Palette de commandes',
  'merchant.commandPalette.placeholder': 'Rechercher un écran ou une action…',
  'merchant.commandPalette.empty': 'Aucun résultat.',
  'merchant.commandPalette.navigationGroup': 'Navigation',
  'merchant.commandPalette.preferencesGroup': 'Préférences',
  'merchant.commandPalette.toggleThemeToLight': 'Basculer en mode clair',
  'merchant.commandPalette.toggleThemeToDark': 'Basculer en mode sombre',

  'merchant.common.loadingAria': 'Chargement',
  'merchant.common.noOrdersYet': 'Aucune commande pour le moment',
  'merchant.common.unexpectedError': 'Une erreur inattendue est survenue.',
  'merchant.common.cancel': 'Annuler',
  'merchant.common.confirm': 'Confirmer',
  'merchant.common.save': 'Enregistrer',
  'merchant.common.edit': 'Modifier',
  'merchant.common.delete': 'Supprimer',
  'merchant.common.accessDenied': 'Accès refusé',

  // ---- merchant: dashboard ----
  'merchant.dashboard.title': 'Tableau de bord',
  'merchant.dashboard.subtitle':
    'Ce que vous avez vendu, vos commandes, et ce qui a besoin de votre attention.',
  'merchant.dashboard.loadError': 'Impossible de charger le tableau de bord',
  'merchant.dashboard.accessDeniedDescription':
    "Votre compte n'a pas la permission de consulter les analytics.",
  'merchant.dashboard.kpiTodayRevenue': 'CA du jour',
  'merchant.dashboard.kpiTodayHint': "{{count}} commande(s) aujourd'hui",
  'merchant.dashboard.kpiMonthRevenue': 'CA du mois',
  'merchant.dashboard.kpiMonthHint': '{{count}} commande(s) ce mois-ci',
  'merchant.dashboard.kpiOrderCount': 'Nombre de commandes',
  'merchant.dashboard.kpiOrderCountHint': "Depuis l'ouverture de la boutique",
  'merchant.dashboard.kpiActiveProducts': 'Produits actifs',
  'merchant.dashboard.trendStable': 'Stable vs hier',
  'merchant.dashboard.trendChange': '{{value}}% vs hier',
  'merchant.dashboard.trendNewActivity': 'Nouvelle activité vs hier',
  'merchant.dashboard.statusByStatusTitle': 'Commandes par statut',
  'merchant.dashboard.statusByStatusSubtitle': "Toutes les commandes, depuis l'ouverture",
  'merchant.dashboard.statusBreakdown.emptyDescription':
    'La répartition par statut apparaîtra ici dès la première commande.',
  'merchant.dashboard.kpiSoonBadge': 'Bientôt',
  'merchant.dashboard.kpiSoonDefaultReason': 'Module pas encore construit.',

  'merchant.dashboard.alerts.title': 'Alertes',
  'merchant.dashboard.alerts.checking': 'Vérification en cours…',
  'merchant.dashboard.alerts.allClear':
    'Rien à signaler — stock, commandes et coupons sont sous contrôle.',
  'merchant.dashboard.alerts.lowStockOne': '1 produit à surveiller côté stock',
  'merchant.dashboard.alerts.lowStockMany': '{{count}} produits à surveiller côté stock',
  'merchant.dashboard.alerts.lowStockOutOfStock': 'Dont {{count}} en rupture.',
  'merchant.dashboard.alerts.lowStockBelowThreshold': "Stock disponible sous le seuil d'alerte.",
  'merchant.dashboard.alerts.actionableOrdersOne': '1 commande à traiter',
  'merchant.dashboard.alerts.actionableOrdersMany': '{{count}} commandes à traiter',
  'merchant.dashboard.alerts.actionableOrdersDescription':
    "En attente de préparation ou d'emballage.",
  'merchant.dashboard.alerts.expiringCouponsOne': '1 coupon expire bientôt',
  'merchant.dashboard.alerts.expiringCouponsMany': '{{count}} coupons expirent bientôt',
  'merchant.dashboard.alerts.expiringCouponsDescription': 'Dans les {{days}} prochains jours.',

  'merchant.dashboard.quickActions.title': 'Actions rapides',
  'merchant.dashboard.quickActions.newProduct': 'Nouveau produit',
  'merchant.dashboard.quickActions.newCoupon': 'Nouveau coupon',
  'merchant.dashboard.quickActions.viewOrders': 'Voir les commandes',

  'merchant.dashboard.salesTrend.loadError': "Impossible de charger l'évolution des ventes",
  'merchant.dashboard.salesTrend.title': 'Evolution des ventes',
  'merchant.dashboard.salesTrend.subtitle':
    '14 derniers jours — commandes confirmées, hors annulations',
  'merchant.dashboard.salesTrend.legendCurrency': 'CA (TND)',
  'merchant.dashboard.salesTrend.emptyTitle': 'Aucune vente sur cette période',
  'merchant.dashboard.salesTrend.emptyDescription':
    'Le graphique se remplira dès votre première commande non annulée.',
  'merchant.dashboard.salesTrend.chartAria':
    "Graphique de l'évolution des ventes sur les 14 derniers jours",
  'merchant.dashboard.salesTrend.totalLabel': 'Total sur la période :',

  'merchant.dashboard.topProducts.title': 'Top produits',
  'merchant.dashboard.topProducts.emptyTitle': 'Aucun produit vendu pour le moment',
  'merchant.dashboard.topProducts.emptyDescription':
    'Le classement apparaîtra ici dès la première commande non annulée.',
  'merchant.dashboard.topProducts.soldCount': '{{count}} vendu{{plural}}',

  // ---- merchant: orders ----
  'merchant.orders.title': 'Commandes',
  'merchant.orders.subtitle': 'Suivez et traitez les commandes reçues.',
  'merchant.orders.filterAllStatuses': 'Tous les statuts',
  'merchant.orders.filterAria': 'Filtrer par statut',
  'merchant.orders.loadError': 'Impossible de charger les commandes',
  'merchant.orders.accessDeniedDescription':
    "Votre compte n'a pas la permission de gérer les commandes.",
  'merchant.orders.paginationSummary': 'Commandes {{from}}–{{to}} sur {{total}}',
  'merchant.orders.paginationPageSuffix': ' · Page {{page}} sur {{pages}}',
  'merchant.orders.prev': 'Précédent',
  'merchant.orders.next': 'Suivant',
  'merchant.orders.table.colOrder': 'Commande',
  'merchant.orders.table.colCustomer': 'Client',
  'merchant.orders.table.colStatus': 'Statut',
  'merchant.orders.table.colTotal': 'Total',
  'merchant.orders.table.colDate': 'Date',
  'merchant.orders.itemCount': '{{count}} article{{plural}}',
  'merchant.orders.actionProcessing': 'Marquer en préparation',
  'merchant.orders.actionPacked': 'Passer en livraison',
  'merchant.orders.completed': 'Terminée',
  'merchant.orders.noActionNeeded': 'Aucune action requise',
  'merchant.orders.updateModalTitle': 'Mettre à jour la commande',
  'merchant.orders.statusUpdated': 'Statut de la commande mis à jour.',
  'merchant.orders.targetProcessing': 'Marquer « En préparation »',
  'merchant.orders.targetPacked': 'Passer « En livraison »',
  'merchant.orders.targetCancel': 'Annuler la commande',
  'merchant.orders.reasonLabel': 'Motif',

  // ---- merchant: store ----
  'merchant.store.subtitleNew': 'Configurez votre boutique pour commencer à vendre.',
  'merchant.store.subtitleExisting': 'Gérez les informations publiques de votre boutique.',
  'merchant.store.loadError': 'Impossible de charger la boutique',
  'merchant.store.updated': 'Boutique mise à jour.',
  'merchant.store.nextStep.title': 'Boutique enregistrée',
  'merchant.store.nextStep.description':
    'Étape suivante recommandée : créez votre première catégorie pour organiser vos produits.',
  'merchant.store.nextStep.cta': 'Créer une catégorie',
  'merchant.store.ownerOnlyNotice':
    "Seul le propriétaire de la boutique peut modifier ces informations. Vous pouvez consulter le formulaire ci-dessous, mais pas l'enregistrer.",
  'merchant.store.createSubmit': 'Créer la boutique',
  'merchant.store.saveSubmit': 'Enregistrer les modifications',

  'merchant.store.form.identity': 'Identité',
  'merchant.store.form.essentials': 'Informations essentielles',
  'merchant.store.form.general': 'Informations générales',
  'merchant.store.form.nameLabel': 'Nom de la boutique',
  'merchant.store.form.slugLabel': 'Slug',
  'merchant.store.form.slugHint': "Utilisé dans l'URL publique — /shop/:slug.",
  'merchant.store.form.prefillNotice':
    "Nous avons préparé votre boutique à partir de votre compte « {{name}} ». Vérifiez le nom et l'adresse, changez-les si vous le souhaitez, puis créez la boutique.",
  'merchant.store.form.slugManualHint':
    'Ce nom ne contient pas de lettres latines : choisissez une adresse en lettres latines, chiffres et tirets (ex. ma-boutique).',
  'merchant.store.form.slugTaken':
    'Cette adresse est déjà utilisée par une autre boutique. Choisissez-en une autre.',
  'merchant.store.form.descriptionLabel': 'Description',
  'merchant.store.form.logoUrlLabel': 'Logo',
  'merchant.store.form.logoUrlHint':
    'Affiché dans l’en-tête, la bannière d’accueil et le pied de page de votre boutique. Idéal : image carrée d’au moins 400 px.',
  'merchant.images.belowRecommended':
    '« {{name}} » est enregistrée, mais elle est petite ({{width}} × {{height}} px) : elle risque d’être floue une fois affichée. Recommandé : au moins {{recommendedWidth}} × {{recommendedHeight}} px.',
  'merchant.store.form.previewUnavailable':
    'L’image est enregistrée, mais son aperçu n’a pas pu être chargé.',
  'merchant.store.form.taglineLabel': 'Slogan',
  'merchant.store.form.taglineHint':
    'Une phrase courte affichée sous le nom de votre boutique (160 caractères max).',
  'merchant.store.form.story': 'Notre histoire',
  'merchant.store.form.storyTitleLabel': 'Titre',
  'merchant.store.form.storyTitleHint':
    'Laissez vide pour afficher « Notre histoire » (120 caractères max).',
  'merchant.store.form.storyBodyLabel': 'Votre histoire',
  'merchant.store.form.storyBodyHint':
    'Qui êtes-vous, que vendez-vous, pourquoi existez-vous ? Séparez les paragraphes par une ligne vide (2 000 caractères max, texte simple).',
  'merchant.store.form.whatsappLabel': 'WhatsApp',
  'merchant.store.form.whatsappHint':
    'Avec l’indicatif, ex. +216 22 123 456. Un bouton WhatsApp apparaît sur votre boutique.',
  'merchant.store.form.phoneLabel': 'Téléphone',
  'merchant.store.form.emailLabel': 'Email',
  'merchant.store.form.addressLabel': 'Adresse',
  'merchant.store.form.seo': 'Référencement (SEO)',
  'merchant.store.form.seoTitleLabel': 'Titre SEO',
  'merchant.store.form.seoDescriptionLabel': 'Description SEO',
  'merchant.store.form.stockDisplayPolicy': 'Affichage du stock sur la boutique',
  'merchant.store.form.stockDisplayPolicy.exact': 'Quantité exacte',
  'merchant.store.form.stockDisplayPolicy.exact.hint':
    'Toujours afficher le nombre réel d’unités disponibles.',
  'merchant.store.form.stockDisplayPolicy.lowStockOnly': '"Plus que X" en stock faible',
  'merchant.store.form.stockDisplayPolicy.lowStockOnly.hint':
    'Le chiffre exact n’apparaît que lorsque le stock est faible ou épuisé — recommandé.',
  'merchant.store.form.stockDisplayPolicy.hidden': 'Jamais afficher la quantité',
  'merchant.store.form.stockDisplayPolicy.hidden.hint':
    'Seul l’état (en stock / stock faible / épuisé) est visible, jamais le chiffre.',
  'merchant.store.form.deliveryFeeLabel': 'Frais de livraison (TND)',
  'merchant.store.form.deliveryFeeHint':
    'Ajouté au sous-total de chaque commande. Laissez à 0 pour ne facturer aucun frais de livraison.',
  'merchant.store.form.branding': 'Identité visuelle',
  'merchant.store.form.brandingAdvanced': 'Mode avancé',
  'merchant.store.form.bannerUrlLabel': 'Bannière de la page d’accueil',
  'merchant.store.form.bannerUrlHint':
    'Affichée en pleine largeur en haut de votre page d’accueil. Idéal : image large d’au moins 1600 × 600 px.',
  'merchant.store.form.colorPrimaryLabel': 'Couleur principale',
  'merchant.store.form.colorPrimaryHint': 'Domine la bannière d’accueil et les éléments de marque.',
  'merchant.store.form.colorSecondaryLabel': 'Couleur secondaire',
  'merchant.store.form.colorSecondaryHint': 'Utilisée pour les bandeaux et sections de soutien.',
  'merchant.store.form.colorAccentLabel': 'Couleur accent',
  'merchant.store.form.colorAccentHint': 'Utilisée pour le bouton principal, par défaut.',
  'merchant.store.form.colorTextLabel': 'Couleur du texte',
  'merchant.store.form.colorTextHint': 'Laissez vide pour garder le texte calculé automatiquement.',
  'merchant.store.form.colorButtonLabel': 'Couleur des boutons',
  'merchant.store.form.colorButtonHint': 'Reprend la couleur accent si laissée vide.',
  'merchant.store.form.colorBadgeLabel': 'Couleur des badges',
  'merchant.store.form.colorBadgeHint': 'Reprend la couleur secondaire si laissée vide.',
  'merchant.store.form.colorPromoLabel': 'Couleur des promotions',
  'merchant.store.form.colorPromoHint': 'Reprend la couleur accent si laissée vide.',
  'merchant.store.form.colorVariantLabel': 'Couleur des variantes',
  'merchant.store.form.colorVariantHint':
    'Anneau de sélection d’une variante (taille, couleur…). Reprend la couleur accent si laissée vide.',
  'merchant.store.form.colorReset': 'Réinitialiser',

  'merchant.store.health.title': 'Santé de la boutique',
  'merchant.store.health.storeConfigured': 'Boutique configurée',
  'merchant.store.health.storeConfiguredHint':
    'Renseignez votre boutique dans le formulaire ci-dessus.',
  'merchant.store.health.categoriesPresent': 'Catégories présentes',
  'merchant.store.health.categoriesPresentHint': 'Créez au moins une catégorie.',
  'merchant.store.health.productsPresent': 'Produits présents',
  'merchant.store.health.productsPresentHint': 'Créez au moins un produit.',
  'merchant.store.health.activeProductsHint': 'Publiez au moins un produit (statut « Actif »).',
  'merchant.store.health.variantsPresent': 'Variantes présentes',
  'merchant.store.health.variantsPresentHint': 'Ajoutez au moins une variante à un produit.',
  'merchant.store.health.stockAvailableHint':
    'Déclarez du stock disponible sur au moins une variante.',

  'merchant.store.visibility.label': 'Statut de la boutique',
  'merchant.store.visibility.active': 'Active — visible publiquement',
  'merchant.store.visibility.pending': "En attente d'approbation — pas encore visible publiquement",
  'merchant.store.visibility.suspended': 'Suspendue — non visible publiquement',
  'merchant.store.visibility.refused': 'Refusée — non visible publiquement',
  'merchant.share.refused.title': 'Votre demande a été refusée',
  'merchant.share.refused.text':
    "La plateforme n'a pas approuvé cette boutique : elle ne sera pas visible du public. Contactez le support de la plateforme pour en savoir plus.",
  'merchant.store.visibility.publicCatalog': 'Catalogue public :',
  'merchant.share.readyTitle': 'Votre boutique est en ligne',
  'merchant.share.readySubtitle':
    'Partagez ce lien avec vos clients : sur WhatsApp, Facebook, Telegram ou partout ailleurs.',
  'merchant.share.linkLabel': 'Lien public de votre boutique',
  'merchant.share.copy': 'Copier le lien',
  'merchant.share.copied': 'Lien copié',
  'merchant.share.copyFailed': 'Copie impossible : sélectionnez le lien et copiez-le à la main.',
  'merchant.share.open': 'Ouvrir ma boutique',
  'merchant.share.newTab': "(s'ouvre dans un nouvel onglet)",
  'merchant.share.shareOn': 'Partager sur',
  'merchant.share.whatsapp': 'WhatsApp',
  'merchant.share.facebook': 'Facebook',
  'merchant.share.telegram': 'Telegram',
  'merchant.share.message': 'Découvrez ma boutique {{name}} :',
  'merchant.share.noStore.title': 'Créez votre boutique pour obtenir votre lien public',
  'merchant.share.noStore.text':
    "Renseignez le nom et l'adresse de votre boutique : son lien à partager apparaîtra ici.",
  'merchant.share.noStore.cta': 'Créer ma boutique',
  'merchant.share.pending.title': 'Votre boutique attend son activation',
  'merchant.share.pending.text':
    "Dès qu'elle sera activée, votre lien public apparaîtra ici, prêt à être partagé.",
  'merchant.share.suspended.title': 'Votre boutique est suspendue',
  'merchant.share.suspended.text':
    "Elle n'est plus visible du public. Contactez le support de la plateforme pour la réactiver.",
  'merchant.share.noProduct.title':
    'Ajoutez votre premier produit pour mettre la boutique en ligne',
  'merchant.share.noProduct.text':
    "Votre lien public apparaîtra ici dès qu'un produit sera visible dans le catalogue.",
  'merchant.share.noProduct.cta': 'Ajouter un produit',
  'merchant.share.unpublished.title': 'Publiez votre catalogue pour mettre la boutique en ligne',
  'merchant.share.unpublished.text':
    'Vos produits sont encore en brouillon ou inactifs. Passez-en au moins un au statut « Actif » : votre lien public apparaîtra ici.',
  'merchant.share.unpublished.cta': 'Voir mes produits',

  // ---- merchant: categories ----
  'merchant.categories.subtitle':
    'Organisez votre catalogue en catégories et sous-catégories, visibles sur votre boutique publique.',
  'merchant.categories.newCategory': 'Nouvelle catégorie',
  'merchant.categories.loadError': 'Impossible de charger les catégories',
  'merchant.categories.accessDeniedDescription':
    "Votre compte n'a pas la permission de gérer le catalogue.",
  'merchant.categories.emptyTitle': 'Aucune catégorie pour le moment',
  'merchant.categories.emptyDescription':
    'Créez votre première catégorie pour commencer à organiser votre catalogue (ex. Mode, Électronique, Maison).',
  'merchant.categories.editTitle': 'Modifier la catégorie',
  'merchant.categories.createAction': 'Créer',
  'merchant.categories.updated': 'Catégorie mise à jour.',
  'merchant.categories.created': 'Catégorie créée.',
  'merchant.categories.nextStep.title': 'Catégorie créée',
  'merchant.categories.nextStep.description':
    'Étape suivante recommandée : créez votre premier produit.',
  'merchant.categories.nextStep.cta': 'Créer un produit',
  'merchant.categories.deleted': 'Catégorie supprimée.',
  'merchant.categories.deleteConfirm':
    'Supprimer la catégorie « {{name}} » ? Cette action est irréversible.{{warning}}',
  'merchant.categories.deleteWarning':
    ' {{count}} produit(s) sont actuellement assignés à cette catégorie et perdront cette assignation.',
  'merchant.categories.form.nameLabel': 'Nom',
  'merchant.categories.form.slugHint': "Utilisé dans l'URL publique de la catégorie.",
  'merchant.categories.form.imageLabel': 'Image',
  'merchant.categories.form.imageHint':
    "URL d'une image déjà hébergée (aucun envoi de fichier ici).",
  'merchant.categories.form.parentLabel': 'Catégorie parente',
  'merchant.categories.form.parentHint': 'Laissez vide pour une catégorie racine.',
  'merchant.categories.form.activeCheckbox': 'Catégorie active (visible sur la boutique publique)',
  'merchant.categories.table.colParent': 'Parent',
  'merchant.categories.table.colProducts': 'Produits',
  'merchant.categories.active': 'Active',
  'merchant.categories.inactive': 'Inactive',

  // ---- merchant: products ----
  'merchant.products.subtitle': 'Gérez le catalogue de votre boutique.',
  'merchant.products.newProduct': 'Nouveau produit',
  'merchant.products.loadError': 'Impossible de charger les produits',
  'merchant.products.emptyTitle': 'Aucun produit pour le moment',
  'merchant.products.emptyDescription':
    'Créez votre premier produit pour commencer à construire votre catalogue.',
  'merchant.products.createAction': 'Créer un produit',
  'merchant.products.noMatchTitle': 'Aucun produit ne correspond à ces filtres',
  'merchant.products.noMatchDescription':
    'Essayez une autre recherche ou réinitialisez les filtres.',
  'merchant.products.table.colProduct': 'Produit',
  'merchant.products.table.colPrice': 'Prix',
  'merchant.products.table.colStock': 'Stock',
  'merchant.products.table.colCategory': 'Catégorie',
  'merchant.products.table.colLastModified': 'Dernière modification',
  'merchant.products.table.colFeatured': 'Coup de cœur',
  'merchant.products.featured.add': 'Mettre en coup de cœur',
  'merchant.products.featured.remove': 'Retirer des coups de cœur',
  'merchant.products.featured.hint':
    "Les coups de cœur (5 au maximum) sont présentés en haut de la page d'accueil de la boutique. Le dernier produit mis en avant est présenté en grand.",
  'merchant.products.featured.inactiveNote':
    "Ce produit n'est pas actif : il n'apparaîtra dans les coups de cœur de la boutique qu'une fois activé.",
  'merchant.products.featured.limitReached':
    "Vous avez déjà {{limit}} coups de cœur. Retirez-en un avant d'en ajouter un autre.",
  'merchant.products.featured.error': 'Impossible de modifier ce coup de cœur. Réessayez.',
  'merchant.products.status.draft': 'Brouillon',
  'merchant.products.status.active': 'Actif',
  'merchant.products.status.inactive': 'Inactif',
  'merchant.products.toolbar.searchPlaceholder': 'Rechercher par nom ou SKU…',
  'merchant.products.toolbar.searchAria': 'Rechercher un produit',
  'merchant.products.toolbar.filterStatusAria': 'Filtrer par statut',
  'merchant.products.toolbar.sortAria': 'Trier',
  'merchant.products.toolbar.statusAll': 'Tous les statuts',
  'merchant.products.toolbar.sortUpdatedDesc': 'Dernière modification (récent)',
  'merchant.products.toolbar.sortUpdatedAsc': 'Dernière modification (ancien)',
  'merchant.products.toolbar.sortNameAsc': 'Nom (A→Z)',
  'merchant.products.toolbar.sortNameDesc': 'Nom (Z→A)',
  'merchant.products.toolbar.sortPriceAsc': 'Prix (croissant)',
  'merchant.products.toolbar.sortPriceDesc': 'Prix (décroissant)',
  'merchant.products.toolbar.sortStockAsc': 'Stock (rupture en premier)',
  'merchant.products.toolbar.sortStockDesc': 'Stock (disponible en premier)',
  'merchant.products.stock.inStock': 'En stock',
  'merchant.products.stock.lowStock': 'Stock faible',
  'merchant.products.stock.outOfStock': 'Rupture',
  'merchant.products.stock.unavailable': 'Indisponible',
  'merchant.products.stock.units': '{{count}} unité{{plural}}',
  'merchant.products.create.title': 'Nouveau produit',
  'merchant.products.create.subtitle': 'Ajoutez un produit à votre catalogue.',
  'merchant.products.create.submitLabel': 'Créer le produit',
  'merchant.products.createdToast': 'Produit « {{name}} » créé.',
  'merchant.products.nextStep.title': 'Produit créé avec succès',
  'merchant.products.nextStep.description':
    'Étape suivante recommandée : configurer votre inventaire.',
  'merchant.products.nextStep.cta': "Accéder à l'inventaire",
  'merchant.products.updatedToast': 'Produit mis à jour.',
  'merchant.products.deleteConfirm': 'Supprimer ce produit ? Cette action est irréversible.',
  'merchant.products.deletedToast': 'Produit supprimé.',
  'merchant.products.notFoundTitle': 'Produit introuvable',
  'merchant.products.detailLoadError': 'Impossible de charger ce produit',
  'merchant.products.imagesHeading': 'Images',
  'merchant.products.imageUploaded': 'Image envoyée.',

  'merchant.products.form.generalInfo': 'Informations générales',
  'merchant.products.form.nameLabel': 'Nom du produit',
  'merchant.products.form.slugHint': "Utilisé dans l'URL publique du produit.",
  'merchant.products.form.skuLabel': 'SKU',
  'merchant.products.form.categoryLabel': 'Catégorie',
  'merchant.products.form.categoryLoading': 'Chargement des catégories…',
  'merchant.products.form.categoryNone': 'Aucune catégorie',
  'merchant.products.form.shortDescriptionLabel': 'Description courte',
  'merchant.products.form.longDescriptionLabel': 'Description longue',
  'merchant.products.form.brandLabel': 'Marque',
  'merchant.products.form.priceAndStatus': 'Prix et statut',
  'merchant.products.form.priceLabel': 'Prix (TND)',
  'merchant.products.form.compareAtPriceLabel': 'Prix barré',
  'merchant.products.form.compareAtPriceHint': 'Prix de comparaison affiché barré.',
  'merchant.products.form.statusDraftOption': 'Brouillon (non visible publiquement)',
  'merchant.products.form.statusActiveOption': 'Actif (visible dans le catalogue public)',

  'merchant.products.images.sending': 'Envoi…',
  'merchant.products.images.error': 'Erreur',
  'merchant.products.images.uploadFailed': "Échec de l'envoi.",
  'merchant.products.images.dropzone': 'Glissez-déposez une image, ou cliquez pour parcourir',
  'merchant.products.images.formats': 'JPEG, PNG ou WebP',
  'merchant.products.images.loadError': 'Impossible de charger les images',
  'merchant.products.images.empty': 'Aucune image envoyée pour ce produit.',
  'merchant.products.images.loadErrorBadge': 'Erreur de chargement',
  'merchant.products.images.position': 'Position {{n}}',

  'merchant.products.variants.title': 'Variantes',
  'merchant.products.variants.add': 'Ajouter une variante',
  'merchant.products.variants.createdToast': 'Variante « {{sku}} » créée.',
  'merchant.products.variants.deleteConfirm': 'Supprimer cette variante ?',
  'merchant.products.variants.deletedToast': 'Variante supprimée.',
  'merchant.products.variants.loadError': 'Impossible de charger les variantes',
  'merchant.products.variants.empty': 'Aucune variante pour ce produit',
  'merchant.products.variants.colAttributes': 'Attributs',
  'merchant.products.variants.colSpecificPrice': 'Prix spécifique',
  'merchant.products.variants.skuLabel': 'SKU de la variante',
  'merchant.products.variants.priceOverrideHint': 'Laissez vide pour utiliser le prix du produit.',
  'merchant.products.variants.attributesExample': '(ex. couleur, taille)',
  'merchant.products.variants.attributeKeyPlaceholder': 'Clé (ex. couleur)',
  'merchant.products.variants.attributeValuePlaceholder': 'Valeur (ex. Noir)',
  'merchant.products.variants.removeAttribute': 'Retirer',
  'merchant.products.variants.addAttribute': 'Ajouter un attribut',
  'merchant.products.variants.colStatus': 'Statut',
  'merchant.products.variants.status.active': 'Active',
  'merchant.products.variants.status.inactive': 'Désactivée',
  'merchant.products.variants.activate': 'Activer',
  'merchant.products.variants.deactivate': 'Désactiver',
  'merchant.products.variants.activatedToast': 'Variante activée — de nouveau visible en boutique.',
  'merchant.products.variants.deactivatedToast':
    'Variante désactivée — masquée en boutique, stock et historique conservés.',

  'merchant.products.options.title': 'Options du produit',
  'merchant.products.options.add': 'Ajouter une option',
  'merchant.products.options.namePlaceholder': 'ex. Taille, Couleur',
  'merchant.products.options.displayTypeLabel': "Type d'affichage",
  'merchant.products.options.displayType.button': 'Boutons',
  'merchant.products.options.displayType.colorSwatch': 'Pastilles de couleur',
  'merchant.products.options.rename': 'Renommer',
  'merchant.products.options.delete': "Supprimer l'option",
  'merchant.products.options.deleteConfirm':
    "Supprimer cette option ? Impossible si des variantes l'utilisent encore.",
  'merchant.products.options.addValue': 'Ajouter une valeur',
  'merchant.products.options.valuePlaceholder': 'ex. 42, Rouge',
  'merchant.products.options.swatchColor': 'Couleur de la pastille',
  // ---- merchant assistant (chatbot) ----
  'merchant.assistant.open': "Ouvrir l'assistant",
  'merchant.assistant.title': 'Assistant',
  'merchant.assistant.subtitle': 'Gérez votre boutique en conversation',
  'merchant.assistant.close': "Fermer l'assistant",
  'merchant.assistant.newConversation': 'Nouvelle conversation',
  'merchant.assistant.unavailable':
    "L'assistant n'est pas configuré sur ce serveur. Contactez l'administrateur de la plateforme.",
  'merchant.assistant.welcome':
    'Bonjour ! Demandez-moi ce que vous voulez faire : créer un produit, ajuster un stock, suivre une commande, créer un coupon… Je vous demanderai confirmation avant toute modification.',
  'merchant.assistant.privacy':
    "Vos messages et les données de la boutique nécessaires à la réponse sont traités par un service d'IA externe (Anthropic). N'y écrivez jamais de mot de passe.",
  'merchant.assistant.placeholder': 'Écrivez votre demande…',
  'merchant.assistant.send': 'Envoyer',
  'merchant.assistant.thinking': "L'assistant réfléchit…",
  'merchant.assistant.operation.succeeded': 'réussi',
  'merchant.assistant.operation.failed': 'échec (statut {{status}})',
  'merchant.assistant.secret.hint':
    'Notez-le maintenant : il ne sera plus jamais affiché. Il n’est pas transmis à l’assistant.',
  'merchant.assistant.secret.copy': 'Copier',
  'merchant.assistant.secret.copied': 'Copié',
  'merchant.assistant.action.openPage': 'Ouvrir la page',
  'merchant.assistant.action.uploadPhoto': 'Choisir une photo',
  'merchant.assistant.action.uploadLogo': 'Choisir le logo',
  'merchant.assistant.action.uploadBanner': 'Choisir la bannière',
  'merchant.assistant.action.uploaded': 'Image envoyée.',
  'merchant.assistant.action.downloadInvoice': 'Télécharger la facture (PDF)',
  'merchant.assistant.pending.title': 'À confirmer',
  'merchant.assistant.pending.confirm': 'Confirmer',
  'merchant.assistant.pending.cancel': 'Annuler',
  'merchant.assistant.notice.refusal': "L'assistant ne peut pas répondre à cette demande.",
  'merchant.assistant.notice.outputLimit': 'La réponse a été interrompue ; rien n’a été exécuté.',
  'merchant.assistant.notice.stepLimit':
    'Demande trop longue à traiter en une fois : reformulez-la en plusieurs étapes.',
  'merchant.assistant.notice.conversationFull':
    'Cette conversation est pleine : commencez une nouvelle conversation.',
  'merchant.assistant.notice.declined': 'Opération annulée : rien n’a été modifié.',
  'merchant.assistant.notice.conversationExpired':
    'Cette conversation a expiré : la suivante repart de zéro.',
  'merchant.assistant.notice.nothingPending':
    'Il n’y a plus rien à confirmer : cette proposition a déjà été traitée ou a expiré.',
  'merchant.products.options.editValue': 'Modifier',
  'merchant.products.options.deleteValue': 'Supprimer',
  'merchant.products.options.deleteValueConfirm':
    "Supprimer cette valeur ? Impossible si des variantes l'utilisent encore.",
  'merchant.products.options.empty':
    'Aucune option définie — ajoutez-en une pour générer des variantes.',
  'merchant.products.options.createdToast': 'Option « {{name}} » créée.',
  'merchant.products.options.deletedToast': 'Option supprimée.',
  'merchant.products.options.valueCreatedToast': 'Valeur « {{value}} » ajoutée.',
  'merchant.products.options.valueDeletedToast': 'Valeur supprimée.',
  'merchant.products.options.autoGeneratedBadge': 'Regroupée automatiquement',
  'merchant.products.options.loadError': 'Impossible de charger les options',

  'merchant.products.generator.title': 'Générateur de variantes',
  'merchant.products.generator.openButton': 'Générer des variantes',
  'merchant.products.generator.selectValues':
    'Sélectionnez les valeurs à combiner pour chaque option.',
  'merchant.products.generator.stepSelect': 'Sélection',
  'merchant.products.generator.stepPreview': 'Aperçu',
  'merchant.products.generator.previewTitle': 'Combinaisons à créer',
  'merchant.products.generator.colCombination': 'Combinaison',
  'merchant.products.generator.colSku': 'SKU',
  'merchant.products.generator.colPrice': 'Prix spécifique',
  'merchant.products.generator.noOptions':
    'Créez au moins une option avec des valeurs avant de générer des variantes.',
  'merchant.products.generator.noCombinations': 'Sélectionnez au moins une valeur par option.',
  'merchant.products.generator.next': 'Aperçu',
  'merchant.products.generator.back': 'Retour',
  'merchant.products.generator.generate': 'Créer {{count}} variante(s)',
  'merchant.products.generator.generatedToast': '{{count}} variante(s) créée(s).',
  'merchant.products.generator.duplicateSkuHint': 'Chaque SKU doit être unique dans la boutique.',

  'merchant.products.bulkEdit.title': 'Édition en masse',
  'merchant.products.bulkEdit.openButton': 'Édition en masse',
  'merchant.products.bulkEdit.selectAll': 'Tout sélectionner',
  'merchant.products.bulkEdit.selectedCount': '{{count}} variante(s) sélectionnée(s)',
  'merchant.products.bulkEdit.noSelection': 'Sélectionnez au moins une variante.',
  'merchant.products.bulkEdit.priceModeLabel': 'Prix',
  'merchant.products.bulkEdit.priceMode.none': 'Ne pas modifier',
  'merchant.products.bulkEdit.priceMode.relative': 'Ajustement relatif (+/-)',
  'merchant.products.bulkEdit.priceMode.absolute': 'Nouveau prix fixe',
  'merchant.products.bulkEdit.priceAmountLabel': 'Montant',
  'merchant.products.bulkEdit.stockDeltaLabel': 'Ajustement de stock',
  'merchant.products.bulkEdit.stockDeltaHint':
    'Positif pour réapprovisionner, négatif pour retirer.',
  'merchant.products.bulkEdit.thresholdLabel': 'Seuil de stock faible',
  'merchant.products.bulkEdit.statusLabel': 'Statut',
  'merchant.products.bulkEdit.status.unchanged': 'Ne pas modifier',
  'merchant.products.bulkEdit.status.activate': 'Activer',
  'merchant.products.bulkEdit.status.deactivate': 'Désactiver',
  'merchant.products.bulkEdit.reasonLabel': 'Motif (optionnel)',
  'merchant.products.bulkEdit.apply': 'Appliquer',
  'merchant.products.bulkEdit.appliedToast': '{{count}} variante(s) mise(s) à jour.',

  'merchant.products.reviewBanner.title': 'Vérifiez vos options',
  'merchant.products.reviewBanner.description':
    'Nous avons regroupé automatiquement les attributs de vos variantes existantes en options structurées. Vérifiez-les avant de continuer — vos variantes restent vendables entre-temps.',
  'merchant.products.reviewBanner.dismiss': "J'ai vérifié",
  'merchant.products.reviewBanner.dismissedToast': 'Options marquées comme vérifiées.',

  // ---- merchant: inventory ----
  'merchant.inventory.subtitle': "Stock disponible et seuils d'alerte pour tous vos produits.",
  'merchant.inventory.loadError': "Impossible de charger l'inventaire",
  'merchant.inventory.emptyTitle': 'Aucun produit à afficher',
  'merchant.inventory.emptyDescription':
    'Ajoutez un produit pour commencer à suivre son stock ici.',
  'merchant.inventory.table.colStockAvailable': 'Stock disponible',
  'merchant.inventory.table.colAlertThreshold': "Seuil d'alerte",
  'merchant.inventory.none': 'Aucun',
  'merchant.inventory.adjust': 'Ajuster',
  'merchant.inventory.drawer.title': 'Ajuster le stock — {{name}}{{skuSuffix}}',
  'merchant.inventory.drawer.adjustmentLabel': 'Ajustement',
  'merchant.inventory.drawer.adjustmentHint':
    'Positif pour ajouter du stock, négatif pour en retirer (ex. -5).',
  'merchant.inventory.drawer.reasonPlaceholder': 'Ex. réception fournisseur',
  'merchant.inventory.drawer.thresholdLabel': "Seuil minimum d'alerte",
  'merchant.inventory.drawer.thresholdHint': 'Laissez vide pour conserver le seuil actuel.',
  'merchant.inventory.drawer.apply': 'Appliquer',
  'merchant.inventory.adjustedToast':
    'Stock ajusté : {{available}} disponible(s), {{reserved}} réservé(s).',

  // ---- admin: shared layout ----
  'admin.sidebar.subtitle': 'Administration plateforme',
  'admin.topbar.label': 'Espace Platform Admin',

  // ---- admin: platform accounts (lot B1) ----
  'admin.nav.administrators': 'Administrateurs',
  'admin.nav.dashboard': 'Tableau de bord',
  'admin.dashboard.title': 'Tableau de bord',
  'admin.dashboard.subtitle':
    "La plateforme en un coup d'œil : activité, revenus, marchands, administrateurs et sécurité.",
  'admin.dashboard.updatedNow': "Mis à jour à l'instant",
  'admin.dashboard.updatedOneSecond': 'Mis à jour il y a 1 seconde',
  'admin.dashboard.updatedSeconds': 'Mis à jour il y a {{seconds}} secondes',
  'admin.dashboard.updatedMinutes': 'Mis à jour il y a {{minutes}} min',
  'admin.dashboard.refresh': 'Actualiser',
  'admin.dashboard.refreshing': 'Actualisation…',
  'admin.dashboard.cacheNote': 'Les chiffres sont recalculés au plus toutes les 60 secondes.',
  'admin.dashboard.loading': 'Chargement du tableau de bord…',
  'admin.dashboard.error.title': 'Impossible de charger le tableau de bord',
  'admin.dashboard.error.forbidden': 'Ce tableau de bord est réservé au superadmin.',
  'admin.dashboard.error.text':
    "Réessayez dans un instant. Si le problème persiste, contactez l'exploitation.",
  'admin.dashboard.retry': 'Réessayer',
  'admin.dashboard.empty.title': 'Aucune donnée pour le moment',
  'admin.dashboard.empty.text':
    'Les chiffres apparaîtront dès que la plateforme aura des marchands et des commandes.',
  'admin.dashboard.chartLoadError': 'Les courbes ne sont pas disponibles pour le moment.',
  'admin.dashboard.section.financial': 'Financier',
  'admin.dashboard.section.merchants': 'Marchands',
  'admin.dashboard.section.actions': 'À traiter',
  'admin.dashboard.section.trends': 'Évolution',
  'admin.dashboard.section.admins': 'Administrateurs',
  'admin.dashboard.section.quality': 'Qualité des données',
  'admin.dashboard.section.security': 'Sécurité',
  'admin.dashboard.section.products': 'Produits',
  'admin.dashboard.section.topMerchants': "Top marchands par chiffre d'affaires",
  'admin.dashboard.fin.salesTotal': 'Ventes totales',
  'admin.dashboard.fin.revenueToday': 'CA du jour',
  'admin.dashboard.fin.revenueMonth': 'CA du mois',
  'admin.dashboard.fin.revenueYear': "CA de l'année",
  'admin.dashboard.fin.averageBasket': 'Panier moyen',
  'admin.dashboard.fin.ordersTotal': 'Commandes (total)',
  'admin.dashboard.fin.ordersToday': 'Commandes du jour',
  'admin.dashboard.fin.excludesCancelled': 'Hors commandes annulées',
  'admin.dashboard.fin.cancelled': '{{count}} commande(s) annulée(s) non comptée(s)',
  'admin.dashboard.mer.total': 'Total marchands',
  'admin.dashboard.mer.active': 'Actifs',
  'admin.dashboard.mer.pending': 'En attente',
  'admin.dashboard.mer.suspended': 'Suspendus',
  'admin.dashboard.mer.unassigned': 'Non attribués',
  'admin.dashboard.mer.openList': 'Ouvrir la liste',
  'admin.dashboard.mer.withStore': '{{count}} avec une boutique en ligne',
  'admin.dashboard.mer.refused': '{{count}} refusé(s)',
  'admin.dashboard.adm.total': 'Administrateurs',
  'admin.dashboard.adm.active': 'Actifs',
  'admin.dashboard.adm.disabled': 'Désactivés',
  'admin.dashboard.adm.topTitle': 'Top 5 administrateurs par nombre de marchands',
  'admin.dashboard.adm.allTitle': 'Tous les administrateurs',
  'admin.dashboard.adm.showAll': 'Voir les {{count}} administrateurs',
  'admin.dashboard.adm.showTop': 'Afficher le top 5',
  'admin.dashboard.adm.capped':
    'Les {{shown}} administrateurs gérant le plus de boutiques sont listés, sur {{total}} au total.',
  'admin.dashboard.adm.cappedLink': 'Voir tous les administrateurs',
  'admin.dashboard.adm.colAdmin': 'Administrateur',
  'admin.dashboard.adm.colMerchants': 'Marchands',
  'admin.dashboard.adm.colActive': 'Actives',
  'admin.dashboard.adm.colPending': 'En attente',
  'admin.dashboard.adm.colSuspended': 'Suspendues',
  'admin.dashboard.adm.colRevenue': 'CA généré',
  'admin.dashboard.adm.colOrders': 'Commandes',
  'admin.dashboard.adm.none': "Aucun administrateur de plateforme n'est enregistré.",
  'admin.dashboard.adm.disabledBadge': 'Désactivé',
  'admin.dashboard.qua.neverSignedIn': 'Marchands jamais connectés',
  'admin.dashboard.qua.neverSignedInHint': "Le propriétaire n'a encore jamais ouvert son espace.",
  'admin.dashboard.qua.provisionalExpired': 'Mots de passe provisoires expirés',
  'admin.dashboard.qua.provisionalExpiredHint':
    'À renouveler par le gestionnaire ou un superadmin.',
  'admin.dashboard.qua.withoutOwner': 'Boutiques sans propriétaire',
  'admin.dashboard.qua.withoutOwnerHint': 'Aucun compte propriétaire actif rattaché.',
  'admin.dashboard.qua.withoutProduct': 'Boutiques sans produit',
  'admin.dashboard.qua.withoutProductHint': 'Hors boutiques refusées.',
  'admin.dashboard.sec.superadmins': 'Superadmins',
  'admin.dashboard.sec.platformAdmins': 'Platform admins',
  'admin.dashboard.sec.logins': "Connexions aujourd'hui",
  'admin.dashboard.sec.loginsHint': 'dont comptes plateforme : {{count}}',
  'admin.dashboard.sec.failed': "Échecs aujourd'hui",
  'admin.dashboard.sec.failedHint':
    'dont comptes plateforme : {{count}} · bloquées par la limitation : {{throttled}}',
  'admin.dashboard.sec.locked': 'Comptes verrouillés',
  'admin.dashboard.sec.promotions': 'Promotions en attente',
  'admin.dashboard.act.unassigned': 'Boutiques non attribuées',
  'admin.dashboard.act.unassignedHint': 'À attribuer à un administrateur avant toute validation.',
  'admin.dashboard.act.pendingOver7d': 'Boutiques en attente > 7 jours',
  'admin.dashboard.act.pendingOver7dHint': 'Créées il y a plus de sept jours, toujours à valider.',
  'admin.dashboard.act.reassignments': 'Réattributions récentes',
  'admin.dashboard.act.reassignmentsHint': '{{last7d}} sur 7 jours · {{last30d}} sur 30 jours',
  'admin.dashboard.act.toHandleTitle': 'Boutiques à traiter',
  'admin.dashboard.act.toHandleTotal': '{{shown}} affichées sur {{total}}',
  'admin.dashboard.act.colShop': 'Boutique',
  'admin.dashboard.act.colStatus': 'Statut',
  'admin.dashboard.act.colReason': 'À faire',
  'admin.dashboard.act.colAge': 'Ancienneté',
  'admin.dashboard.act.colManager': 'Gestionnaire',
  'admin.dashboard.act.reason.unassigned': 'Attribuer à un administrateur',
  'admin.dashboard.act.reason.provisional_expired': 'Renouveler le mot de passe provisoire',
  'admin.dashboard.act.reason.pending_over_7d':
    'Valider ou refuser (en attente depuis plus de 7 jours)',
  'admin.dashboard.act.ageDays': '{{days}} j',
  'admin.dashboard.act.noneToHandle':
    'Rien à traiter : toutes les boutiques sont attribuées et à jour.',
  'admin.dashboard.act.unnamed': 'Sans nom',
  'admin.dashboard.act.noManager': 'Aucun',
  'admin.dashboard.act.recentTitle': 'Dernières réattributions',
  'admin.dashboard.act.recentRow': '{{name}} → {{admin}}',
  'admin.dashboard.act.recentNone': 'Aucune réattribution récente.',
  'admin.dashboard.act.openPortfolios': 'Ouvrir les portefeuilles',
  'admin.dashboard.trend.sales': 'Ventes',
  'admin.dashboard.trend.orders': 'Commandes',
  'admin.dashboard.trend.registrations': 'Nouveaux marchands',
  'admin.dashboard.trend.currency': 'TND',
  'admin.dashboard.trend.range': 'Période',
  'admin.dashboard.trend.r7': '7 jours',
  'admin.dashboard.trend.r30': '30 jours',
  'admin.dashboard.trend.r90': '90 jours',
  'admin.dashboard.trend.r12m': '12 mois',
  'admin.dashboard.trend.aria': '{{title}} : {{total}} sur la période, du {{from}} au {{to}}',
  'admin.dashboard.trend.empty': 'Aucune donnée sur la période.',
  'admin.dashboard.trend.peak': 'Pic : {{value}} le {{date}}',
  'admin.dashboard.trend.tableCaption': 'Données de la courbe « {{title}} »',
  'admin.dashboard.trend.colPeriod': 'Période',
  'admin.dashboard.pro.total': 'Produits',
  'admin.dashboard.pro.active': 'Actifs',
  'admin.dashboard.pro.outOfStock': 'En rupture',
  'admin.dashboard.pro.topTitle': 'Top produits vendus',
  'admin.dashboard.pro.colProduct': 'Produit',
  'admin.dashboard.pro.colMerchant': 'Marchand',
  'admin.dashboard.pro.colQuantity': 'Quantité',
  'admin.dashboard.pro.colRevenue': "Chiffre d'affaires",
  'admin.dashboard.pro.none': 'Aucun produit vendu pour le moment.',
  'admin.dashboard.top.colMerchant': 'Marchand',
  'admin.dashboard.top.colRevenue': "Chiffre d'affaires",
  'admin.dashboard.top.colOrders': 'Commandes',
  'admin.dashboard.top.colManager': 'Gestionnaire',
  'admin.dashboard.top.none': 'Aucune vente pour le moment.',
  'admin.accounts.title': 'Administrateurs de la plateforme',
  'admin.accounts.subtitle':
    'Qui détient un pouvoir sur la plateforme. Créer un administrateur, retirer un rôle, désactiver un compte, et promouvoir un superadmin à deux personnes.',
  'admin.accounts.tab.label': 'Sections des administrateurs',
  'admin.accounts.tab.accounts': 'Comptes',
  'admin.accounts.tab.create': 'Créer un administrateur',
  'admin.accounts.tab.promotions': 'Promotions superadmin',
  'admin.accounts.tab.lookup': "Appartenances d'un compte",
  'admin.accounts.reasonPlaceholder': 'Ex. : revue trimestrielle des accès administrateurs',
  'admin.accounts.reasonRequiredDescription':
    'Indiquez pourquoi vous consultez cette liste : la consultation est auditée.',
  'admin.accounts.loadError': 'Impossible de charger les comptes.',
  'admin.accounts.empty': 'Aucun compte plateforme.',
  'admin.accounts.table.caption': 'Comptes plateforme',
  'admin.accounts.table.colAccount': 'Compte',
  'admin.accounts.table.colRole': 'Rôle',
  'admin.accounts.table.colStatus': 'État',
  'admin.accounts.table.colLastLogin': 'Dernière connexion',
  'admin.accounts.table.neverSignedIn': 'Jamais connecté',
  'admin.accounts.role.superadmin': 'Superadmin',
  'admin.accounts.role.platform_admin': 'Administrateur',
  'admin.accounts.status.active': 'Actif',
  'admin.accounts.status.disabled': 'Désactivé',
  'admin.accounts.status.locked': 'Verrouillé',
  'admin.accounts.provisionalPending': 'Mot de passe provisoire en attente',
  'admin.accounts.you': 'Vous',
  'admin.accounts.action.revoke': 'Retirer le rôle',
  'admin.accounts.action.disable': 'Désactiver',
  'admin.accounts.action.enable': 'Réactiver',
  'admin.accounts.revokeTitle': 'Retirer le rôle plateforme',
  'admin.accounts.revokeWarning':
    "Le compte perd tout pouvoir sur la plateforme et ses sessions sont fermées. Le compte lui-même n'est pas supprimé. Le dernier superadmin ne peut jamais être retiré. Un administrateur qui gère encore des marchands ne peut être ni retiré ni désactivé : transférez d'abord son portefeuille (menu Portefeuilles).",
  'admin.accounts.disableTitle': 'Désactiver le compte',
  'admin.accounts.disableWarning':
    "La personne ne peut plus se connecter et ses sessions sont fermées. Réversible. Le dernier superadmin actif ne peut jamais être désactivé. Un administrateur qui gère encore des marchands ne peut être ni retiré ni désactivé : transférez d'abord son portefeuille (menu Portefeuilles).",
  'admin.accounts.enableTitle': 'Réactiver le compte',
  'admin.accounts.updatedToast': 'Compte mis à jour.',
  'admin.accounts.create.title': 'Créer un administrateur',
  'admin.accounts.create.note':
    "Le nouveau compte reçoit le rôle Administrateur, jamais Superadmin (un superadmin se promeut à deux). Un mot de passe provisoire est généré : il s'affiche une seule fois.",
  'admin.accounts.create.emailLabel': 'Adresse e-mail du nouvel administrateur',
  'admin.accounts.create.displayNameLabel': 'Nom affiché',
  'admin.accounts.create.submit': "Créer l'administrateur",
  'admin.accounts.create.success': 'Administrateur créé.',
  'admin.accounts.create.createAnother': 'Créer un autre administrateur',
  'admin.accounts.create.handover':
    'Transmettez ce mot de passe de vive voix ou par un canal sûr. Il expire et la personne devra en choisir un nouveau à sa première connexion.',
  'admin.promotions.title': 'Promotion à superadmin',
  'admin.promotions.subtitle':
    "Deux superadmins différents : l'un demande, l'autre confirme. Trois superadmins au maximum. Une demande expire.",
  'admin.promotions.requestTitle': 'Demander une promotion',
  'admin.promotions.targetLabel': 'Administrateur à promouvoir',
  'admin.promotions.targetPlaceholder': 'Choisir un administrateur actif',
  'admin.promotions.noCandidates': 'Aucun administrateur actif à promouvoir.',
  'admin.promotions.requestSubmit': 'Demander la promotion',
  'admin.promotions.requestedToast': 'Demande envoyée : un autre superadmin doit la confirmer.',
  'admin.promotions.confirmedToast': 'Promotion confirmée.',
  'admin.promotions.rejectedToast': 'Demande rejetée.',
  'admin.promotions.cancelledToast': 'Demande annulée.',
  'admin.promotions.listTitle': 'Demandes récentes (30 jours)',
  'admin.promotions.empty': 'Aucune demande récente.',
  'admin.promotions.table.caption': 'Demandes de promotion',
  'admin.promotions.table.colTarget': 'Compte à promouvoir',
  'admin.promotions.table.colRequestedBy': 'Demandé par',
  'admin.promotions.table.colStatus': 'État',
  'admin.promotions.table.colExpires': 'Expire le',
  'admin.promotions.status.pending': 'En attente',
  'admin.promotions.status.confirmed': 'Confirmée',
  'admin.promotions.status.rejected': 'Rejetée',
  'admin.promotions.status.cancelled': 'Annulée',
  'admin.promotions.status.expired': 'Expirée',
  'admin.promotions.action.confirm': 'Confirmer',
  'admin.promotions.action.reject': 'Rejeter',
  'admin.promotions.action.cancel': 'Annuler ma demande',
  'admin.promotions.confirmTitle': 'Confirmer la promotion',
  'admin.promotions.confirmWarning':
    'Le compte deviendra superadmin et ses sessions seront fermées. Vous confirmez en tant que second superadmin : vous ne pouvez pas confirmer votre propre demande.',
  'admin.promotions.rejectTitle': 'Rejeter la demande',
  'admin.promotions.cancelTitle': 'Annuler la demande',
  'admin.promotions.waitingForOther': 'Attend un autre superadmin',
  'errors.platformAccountSelf': 'Vous ne pouvez pas faire cela sur votre propre compte.',
  'errors.lastSuperadmin': 'Le dernier superadmin actif ne peut être ni retiré ni désactivé.',
  'errors.accountStatusUnchanged': 'Le compte est déjà dans cet état.',
  'errors.accountHasMerchants':
    "Cet administrateur gère encore des marchands : transférez d'abord son portefeuille.",
  'errors.managerRequired': "Indiquez l'administrateur qui gérera ce marchand.",
  'errors.managerForbidden':
    'Seul un superadmin peut confier un marchand à un autre administrateur.',
  'errors.managerInvalid':
    "Le gestionnaire choisi n'est pas un administrateur de plateforme actif.",
  'errors.portfolioTargetInvalid':
    "L'administrateur choisi n'est pas un administrateur de plateforme actif.",
  'errors.portfolioUnchanged': 'Ces marchands sont déjà gérés par cet administrateur.',
  'errors.portfolioSameAdmin':
    'Le portefeuille ne peut pas être transféré à son propre administrateur.',
  'errors.portfolioEmpty': 'Cet administrateur ne gère aucun marchand.',
  'errors.suspendedBySuperadmin':
    'Ce marchand a été suspendu par un superadmin : seul un superadmin peut le réactiver.',
  'errors.ownerInUse':
    'Le propriétaire a déjà utilisé son compte : seule la réinitialisation par un superadmin est possible.',
  'errors.promotionSelf': 'Personne ne promeut ni ne confirme sa propre promotion.',
  'errors.promotionTargetNotEligible': 'Seul un administrateur actif peut être promu superadmin.',
  'errors.superadminCapReached': 'Il ne peut y avoir plus de 3 superadmins.',
  'errors.noSecondSuperadmin':
    "Une promotion exige un second superadmin pour la confirmer : utilisez la procédure d'opérateur à deux personnes.",
  'errors.promotionAlreadyPending': 'Une promotion est déjà en attente pour ce compte.',
  'errors.promotionNotPending': 'Cette demande a déjà été traitée.',
  'errors.promotionExpired': 'Cette demande a expiré.',
  'errors.promotionSamePerson':
    'Un autre superadmin doit confirmer : personne ne confirme sa propre demande.',

  // ---- admin: access and passwords (lot B2) ----
  'admin.nav.access': 'Accès et mots de passe',
  'admin.access.title': 'Accès et mots de passe',
  'admin.access.subtitle':
    'Dépanner un compte sans toucher à la base : comptes verrouillés, mots de passe provisoires, sessions. Chaque consultation est auditée.',
  'admin.access.tab.label': 'Sections accès et mots de passe',
  'admin.access.tab.locked': 'Comptes verrouillés',
  'admin.access.tab.provisional': 'Mots de passe provisoires',
  'admin.access.tab.lookup': 'Rechercher un compte',
  'admin.access.kind.superadmin': 'Superadmin',
  'admin.access.kind.platform_admin': 'Administrateur',
  'admin.access.kind.shop': 'Compte boutique',
  'admin.access.loadError': 'Impossible de charger la liste.',
  'admin.access.locked.empty': 'Aucun compte verrouillé.',
  'admin.access.locked.caption': 'Comptes verrouillés',
  'admin.access.col.account': 'Compte',
  'admin.access.col.kind': 'Type',
  'admin.access.col.failures': 'Échecs',
  'admin.access.col.lockedUntil': "Verrouillé jusqu'à",
  'admin.access.action.unlock': 'Déverrouiller',
  'admin.access.unlockTitle': 'Déverrouiller le compte',
  'admin.access.unlockWarning':
    "Le compteur d'échecs est remis à zéro et la personne peut se reconnecter tout de suite. Vérifiez son identité avant.",
  'admin.access.unlockedToast': 'Compte déverrouillé.',
  'admin.access.provisional.empty': 'Aucun mot de passe provisoire en cours.',
  'admin.access.provisional.caption': 'Mots de passe provisoires en cours',
  'admin.access.col.expires': 'Expire le',
  'admin.access.provisional.expired': 'Expiré : à renouveler',
  'admin.access.provisional.valid': 'En cours',
  'admin.access.lookup.emailLabel': 'Adresse e-mail du compte',
  'admin.access.lookup.submit': 'Rechercher',
  'admin.access.lookup.notFound': 'Aucun compte avec cette adresse.',
  'admin.access.lookup.reasonFirst':
    "Indiquez d'abord le motif de consultation (audité) ci-dessus.",
  'admin.access.lookup.status': 'État',
  'admin.access.lookup.lastLogin': 'Dernière connexion',
  'admin.access.lookup.locked': "Verrouillé jusqu'à",
  'admin.access.lookup.provisional': 'Mot de passe provisoire en attente',
  'admin.access.sessions.title': 'Sessions du compte',
  'admin.access.sessions.caption': 'Sessions',
  'admin.access.sessions.empty': 'Aucune session.',
  'admin.access.sessions.retention':
    "L'adresse IP et le navigateur sont conservés 90 jours, puis effacés.",
  'admin.access.sessions.colOpened': 'Ouverte le',
  'admin.access.sessions.colLastUsed': 'Dernière activité',
  'admin.access.sessions.colIp': 'Adresse IP',
  'admin.access.sessions.colBrowser': 'Navigateur',
  'admin.access.sessions.colState': 'État',
  'admin.access.sessions.active': 'Active',
  'admin.access.sessions.ended': 'Fermée',
  'admin.access.sessions.noMetadata': 'Effacé (plus de 90 jours)',
  'admin.access.sessions.reason.logout': 'Déconnexion',
  'admin.access.sessions.reason.logout_all': 'Déconnexion partout',
  'admin.access.sessions.reason.token_reuse_detected': 'Vol de jeton détecté',
  'admin.access.sessions.reason.password_changed': 'Mot de passe changé',
  'admin.access.sessions.reason.expired': 'Expirée',
  'admin.access.sessions.reason.role_changed': 'Rôle modifié',
  'admin.access.sessions.reason.account_disabled': 'Compte désactivé',
  'admin.access.sessions.reason.admin_revoked': 'Fermée par un superadmin',
  'admin.access.sessions.revokeAll': 'Fermer toutes les sessions',
  'admin.access.sessions.revokeTitle': 'Fermer toutes les sessions du compte',
  'admin.access.sessions.revokeWarning':
    'La personne est déconnectée partout et devra se reconnecter. Impossible sur votre propre compte.',
  'admin.access.sessions.revokedToast': '{{count}} session(s) fermée(s).',
  'admin.access.reset.action': 'Réinitialiser le mot de passe',
  'admin.access.reset.title': 'Nouveau mot de passe provisoire',
  'admin.access.reset.warning':
    'Le mot de passe actuel cesse de fonctionner, les sessions du compte sont fermées et le verrouillage est levé. La personne devra choisir son propre mot de passe à la première connexion. 3 réinitialisations par jour au maximum.',
  'admin.access.reset.methodLabel': "Comment l'identité de la personne a-t-elle été vérifiée ?",
  'admin.access.reset.methodHint':
    "Hors plateforme : il n'y a pas d'e-mail, c'est la vraie protection. Cette information est enregistrée dans l'audit.",
  'admin.access.reset.method.phone_call': 'Appel téléphonique (numéro connu)',
  'admin.access.reset.method.video_call': 'Visioconférence',
  'admin.access.reset.method.in_person': 'En personne',
  'admin.access.reset.methodPlaceholder': 'Choisir une méthode',
  'admin.access.reset.submit': 'Générer un mot de passe provisoire',
  'admin.access.reset.superadminNote':
    "Un superadmin n'est jamais réinitialisé par l'application : procédure de bris de glace à deux personnes (voir le runbook).",
  'admin.access.reset.shopNote':
    "Le mot de passe d'un propriétaire de boutique se réinitialise depuis la liste des marchands.",
  'errors.superadminNotResettable':
    "Un superadmin n'est jamais réinitialisé par l'application : utilisez la procédure de bris de glace.",
  'errors.accountDisabled': 'Le compte est désactivé.',
  'errors.accountNotLocked': "Le compte n'est pas verrouillé.",
  'errors.insufficientStock':
    'Stock insuffisant : ce retrait ferait passer la quantité disponible sous zéro.',
  'errors.storageUnavailable':
    "Le service d'images est momentanément indisponible. Votre fichier n'est pas en cause : réessayez dans quelques instants.",

  // ---- shopper side: cart, checkout, coupon, order lookup ----
  'errors.cartNotFound':
    'Votre panier est introuvable ou a expiré. Retournez à la boutique pour recommencer.',
  'errors.cartItemNotFound': "Cet article n'est plus dans votre panier.",
  'errors.cartEmpty': 'Votre panier est vide.',
  'errors.productNotAvailable': "Ce produit n'est plus disponible.",
  'errors.stockExceeded': "Il n'y a pas assez de stock pour cette quantité.",
  'errors.variantRequired': 'Choisissez une option (taille, couleur…) avant d’ajouter ce produit.',
  'errors.variantNotFound': "Cette option n'existe pas pour ce produit.",
  'errors.couponNotFound': "Ce code promo n'existe pas.",
  'errors.couponNotYetValid': "Ce code promo n'est pas encore valable.",
  'errors.couponExpired': 'Ce code promo a expiré.',
  'errors.couponMinimumNotMet':
    "Votre commande n'atteint pas le montant minimum requis pour ce code promo.",
  'errors.couponUsageLimitReached': "Ce code promo a atteint sa limite d'utilisation.",
  'errors.orderNotFound': 'Aucune commande ne correspond à ce numéro et à ce téléphone.',

  // ---- account and security (lot B3) ----
  'account.nav': 'Mon compte',
  'account.title': 'Mon compte',
  'account.subtitle': "Vos connexions, vos sessions et l'historique de sécurité de votre compte.",
  'account.changePassword': 'Changer mon mot de passe',
  'account.lastSignIn.title': 'Dernière connexion',
  'account.lastSignIn.value': '{{date}}, depuis {{ip}}',
  'account.lastSignIn.valueNoIp': '{{date}}',
  'account.lastSignIn.none': "C'est votre première connexion.",
  'account.lastSignIn.notice': 'Dernière connexion : {{date}}',
  'account.lastSignIn.dismiss': 'Fermer',
  'account.lastSignIn.hint':
    "Ce n'était pas vous ? Changez votre mot de passe et fermez les autres sessions.",
  'account.loadError': "Impossible de charger l'historique.",
  'account.sessions.title': 'Mes sessions',
  'account.sessions.caption': 'Sessions',
  'account.sessions.retention':
    "L'adresse IP et le navigateur sont conservés 90 jours, puis effacés.",
  'account.sessions.colOpened': 'Ouverte le',
  'account.sessions.colLastUsed': 'Dernière activité',
  'account.sessions.colIp': 'Adresse IP',
  'account.sessions.colBrowser': 'Navigateur',
  'account.sessions.colState': 'État',
  'account.sessions.current': 'Cette session',
  'account.sessions.active': 'Active',
  'account.sessions.ended': 'Fermée',
  'account.sessions.erased': 'Effacé (plus de 90 jours)',
  'account.sessions.empty': 'Aucune session.',
  'account.sessions.revokeOthers': 'Me déconnecter partout ailleurs',
  'account.sessions.revokeOthersHint':
    "Ferme toutes vos autres sessions et garde celle-ci. À faire si vous pensez qu'un autre appareil a accès à votre compte.",
  'account.sessions.revokedToast': '{{count}} autre(s) session(s) fermée(s).',
  'account.events.title': 'Historique de sécurité',
  'account.events.caption': 'Événements de sécurité',
  'account.events.colWhen': 'Quand',
  'account.events.colEvent': 'Événement',
  'account.events.colIp': 'Adresse IP',
  'account.events.empty': 'Aucun événement.',
  'account.event.auth.login_succeeded': 'Connexion réussie',
  'account.event.auth.login_failed': 'Tentative de connexion échouée',
  'account.event.auth.account_locked': "Compte verrouillé (trop d'échecs)",
  'account.event.auth.password_changed': 'Mot de passe changé',
  'account.event.auth.password_change_failed': 'Changement de mot de passe refusé',
  'account.event.auth.password_reset_completed': 'Mot de passe réinitialisé',
  'account.event.auth.sessions_revoked': 'Sessions fermées',
  'account.event.auth.reauth_failed': 'Confirmation du mot de passe refusée',
  'account.event.auth.temporary_password_expired': 'Mot de passe provisoire expiré',
  'account.event.unknown': 'Événement de sécurité',
  'admin.nav.security': 'Sécurité et audit',
  'admin.security.title': 'Sécurité et audit',
  'admin.security.subtitle':
    "Le journal d'audit, les connexions et les alertes. Lecture seule : le journal ne peut ni être modifié ni être effacé, et chaque consultation y est elle-même enregistrée.",
  'admin.security.tab.label': 'Sections sécurité et audit',
  'admin.security.tab.audit': "Journal d'audit",
  'admin.security.tab.events': 'Connexions',
  'admin.security.tab.alerts': 'Alertes',
  'admin.security.reasonPlaceholder': 'Ex. : revue mensuelle de la sécurité',
  'admin.security.reasonRequired':
    'Indiquez pourquoi vous consultez le journal : la consultation est elle-même auditée.',
  'admin.security.loadError': 'Impossible de charger les données.',
  'admin.security.audit.filterAction': 'Action (préfixe)',
  'admin.security.audit.filterActionPlaceholder': 'ex. superadmin. ou auth.login',
  'admin.security.audit.filterTarget': 'Cible (identifiant)',
  'admin.security.audit.filterActor': 'Acteur (UUID)',
  'admin.security.audit.filterTenant': 'Organisation (UUID)',
  'admin.security.audit.filterFrom': 'Du',
  'admin.security.audit.filterTo': 'Au (exclu)',
  'admin.security.audit.apply': 'Appliquer',
  'admin.security.audit.caption': "Journal d'audit",
  'admin.security.audit.colWhen': 'Quand',
  'admin.security.audit.colActor': 'Acteur',
  'admin.security.audit.colAction': 'Action',
  'admin.security.audit.colTarget': 'Cible',
  'admin.security.audit.colIp': 'Adresse IP',
  'admin.security.audit.colDetails': 'Détails',
  'admin.security.audit.anonymous': 'Anonyme',
  'admin.security.audit.empty': 'Aucune entrée pour ces filtres.',
  'admin.security.audit.loadMore': 'Charger les entrées plus anciennes',
  'admin.security.audit.export': 'Exporter en CSV',
  'admin.security.audit.exportHint':
    "5 000 lignes au plus, avec les filtres et le motif ci-dessus. L'export est lui-même enregistré dans le journal.",
  'admin.security.audit.exported': 'Export téléchargé.',
  'admin.security.audit.exportError': "L'export a échoué.",
  'admin.security.events.hours': 'Période',
  'admin.security.events.hoursOption': '{{hours}} h',
  'admin.security.events.totals': 'Totaux',
  'admin.security.events.failedByIp': 'Échecs par adresse IP',
  'admin.security.events.failedByIdentifier': 'Échecs par identifiant (empreinte tronquée)',
  'admin.security.events.empty': 'Rien sur cette période.',
  'admin.security.events.colEvent': 'Événement',
  'admin.security.events.colCount': 'Nombre',
  'admin.security.events.colKey': 'Clé',
  'admin.security.alerts.empty': 'Aucune alerte : rien ne dépasse les seuils.',
  'admin.security.alerts.note':
    "Calculées à chaque consultation à partir du journal et des sessions : rien n'est stocké ni envoyé (pas d'e-mail).",
  'admin.security.severity.critical': 'Critique',
  'admin.security.severity.warning': 'À surveiller',
  'admin.security.severity.info': 'Information',
  'admin.security.alert.login_failures_spike':
    '{{count}} échecs de connexion dans la dernière heure',
  'admin.security.alert.login_failures_single_ip':
    '{{count}} échecs de connexion depuis une même adresse dans la dernière heure',
  'admin.security.alert.accounts_locked': '{{count}} compte(s) verrouillé(s) ces dernières 24 h',
  'admin.security.alert.break_glass_executed':
    'Bris de glace exécuté ({{count}}) ces dernières 24 h',
  'admin.security.alert.last_superadmin_override':
    'Dérogation « dernier superadmin » utilisée ({{count}}) ces dernières 24 h',
  'admin.security.alert.superadmin_promoted': 'Promotion superadmin ({{count}}) ces dernières 24 h',
  'admin.security.alert.platform_role_revoked':
    'Rôle plateforme retiré ({{count}}) ces dernières 24 h',
  'admin.security.alert.platform_account_disabled':
    'Compte plateforme désactivé ({{count}}) ces dernières 24 h',
  'admin.security.alert.platform_admin_created':
    'Administrateur créé ({{count}}) ces dernières 24 h',
  'admin.security.alert.owner_password_reissued':
    'Mot de passe de propriétaire réinitialisé ({{count}}) ces dernières 24 h',
  'admin.security.alert.admin_password_reissued':
    "Mot de passe d'administrateur réinitialisé ({{count}}) ces dernières 24 h",
  'admin.security.alert.promotion_pending':
    '{{count}} promotion(s) superadmin en attente de confirmation',
  'admin.security.alert.superadmin_new_address':
    '{{count}} connexion(s) de superadmin depuis une adresse jamais vue (24 h)',
  'admin.security.alert.no_second_superadmin':
    "Moins de deux superadmins actifs : aucune promotion possible par l'application",
  'admin.security.alert.unknown': 'Alerte de sécurité',

  // ---- admin: nav ----
  'admin.nav.merchants': 'Marchands',

  // ---- admin: auth ----
  'admin.auth.loginTitle': 'Connexion',
  'admin.auth.loginSubtitle': "Accédez à l'administration plateforme.",
  'admin.auth.loginButton': 'Se connecter',
  'admin.auth.passwordLabel': 'Mot de passe',

  // ---- admin: shared reason field ----
  'admin.common.reasonLabel': 'Motif (10 caractères minimum, requis et audité)',
  'admin.common.reasonConsultationLabel':
    'Motif de consultation (10 caractères minimum, requis et audité)',
  'admin.common.reasonRequiredTitle': 'Motif requis',

  // ---- admin: merchants ----
  'admin.merchants.subtitle':
    "File d'approbation et gestion du statut des marchands de la plateforme.",
  'admin.merchants.reasonPlaceholder': "Ex. : traitement de la file d'approbation quotidienne",
  'admin.merchants.filterStatusAria': 'Filtrer par statut',
  'admin.merchants.reasonRequiredDescription':
    'Indiquez un motif de consultation (10 caractères minimum) pour charger la liste — chaque lecture de ce domaine est auditée.',
  'admin.merchants.loadError': 'Impossible de charger les marchands',
  'admin.merchants.accessDeniedDescription':
    "Votre compte n'a pas le rôle Platform Admin, ou la vérification en deux étapes n'a pas été présentée.",
  'admin.merchants.emptyForFilter': 'Aucun marchand pour ce filtre',
  'admin.merchants.paginationSummary': '{{from}}–{{to}} sur {{total}}',
  'admin.merchants.table.colLegalName': 'Raison sociale',
  'admin.merchants.table.colCreatedAt': 'Créé le',
  'admin.merchants.action.approve': 'Approuver',
  'admin.merchants.action.suspend': 'Suspendre',
  'admin.merchants.action.reactivate': 'Réactiver',
  'admin.merchants.updateModalTitle': 'Mettre à jour le statut du marchand',
  'admin.merchants.updatedToast': 'Statut du marchand mis à jour.',
  'admin.merchants.noEmailHint':
    "Aucun e-mail n'est envoyé au marchand pour ce changement : prévenez-le directement si nécessaire.",

  // ---- admin: platform roles ----
  'admin.roles.subtitle': "Consultation des appartenances d'un compte (audité).",
  'admin.roles.lookupSectionTitle': "Consulter les appartenances d'un utilisateur",
  'admin.roles.noMembershipsTitle': 'Aucune appartenance pour cet utilisateur',
  'admin.roles.membershipsTableCaption': 'Appartenances',
  'admin.roles.table.colTenant': 'Boutique (tenant)',
  'admin.roles.userIdLabel': 'Identifiant utilisateur (UUID)',
  'admin.roles.lookupButton': 'Consulter les appartenances',

  // ---- admin : provisionnement d'organisation ----
  'admin.nav.organisations': 'Nouveau marchand',
  'admin.organisations.title': 'Créer un compte marchand',
  'admin.organisations.subtitle':
    "Crée la boutique et le compte de son propriétaire. Un mot de passe provisoire est généré et affiché une seule fois : vous le remettez au marchand, qui devra en choisir un nouveau dès sa première connexion. Aucun e-mail n'est envoyé.",
  'admin.organisations.emailLabel': 'Adresse e-mail du propriétaire',
  'admin.organisations.legalNameLabel': 'Raison sociale',
  'admin.organisations.organisationActiveLabel': 'Approuver la boutique immédiatement',
  'admin.organisations.organisationActiveHint':
    "Décoché, la boutique rejoint la file d'approbation habituelle.",
  'admin.organisations.reasonLabel': 'Motif (audité)',
  'admin.common.confirmPasswordLabel': 'Votre mot de passe (confirmation)',
  'admin.common.confirmPasswordHint':
    "Action sensible : saisissez à nouveau votre propre mot de passe. Il n'est ni enregistré ni affiché.",
  'admin.common.confirmPasswordRequired': 'Saisissez votre mot de passe pour confirmer.',
  'admin.merchants.action.refuse': 'Refuser',
  'admin.merchants.action.resetOwner': 'Mot de passe du propriétaire',
  'admin.merchants.statusRefused': 'Refusée',
  'admin.nav.portfolios': 'Portefeuilles',
  'admin.merchants.scopeAdminInfo':
    "Vous voyez uniquement les marchands de votre portefeuille : ceux que vous avez créés ou qu'un superadmin vous a attribués.",
  'admin.merchants.scopeSuperInfo':
    "Vous voyez tous les marchands de la plateforme, attribués ou non. Vous pouvez les attribuer à un administrateur ; seul l'administrateur gestionnaire valide ou refuse une boutique.",
  'admin.merchants.portfolioEmptyTitle': 'Votre portefeuille est vide',
  'admin.merchants.portfolioEmptyDescription':
    "Vous ne gérez encore aucun marchand. Les marchands que vous créez apparaissent ici. Les boutiques existantes ou en attente ne vous sont pas visibles tant qu'un superadmin ne vous les a pas attribuées.",
  'admin.merchants.portfolioEmptyAction': 'Créer un marchand',
  'admin.merchants.noUnassignedTitle': 'Aucune boutique non attribuée',
  'admin.merchants.searchLabel': 'Rechercher un marchand',
  'admin.merchants.searchPlaceholder': 'Raison sociale (2 caractères minimum)',
  'admin.merchants.scopeLabel': 'Portefeuille',
  'admin.merchants.scopeAll': 'Tous les marchands',
  'admin.merchants.scopeUnassigned': 'Non attribuées',
  'admin.merchants.table.colManager': 'Géré par',
  'admin.merchants.unassigned': 'Non attribuée',
  'admin.merchants.unassignedHint': 'À attribuer à un administrateur avant toute validation.',
  'admin.merchants.pendingForManager': "À valider par l'administrateur gestionnaire.",
  'admin.merchants.selectRowAria': 'Sélectionner {{name}}',
  'admin.merchants.selectAllAria': 'Sélectionner toutes les boutiques de la page',
  'admin.merchants.assignSelection': 'Attribuer la sélection ({{count}})',
  'admin.merchants.action.assign': 'Attribuer',
  'admin.merchants.action.reassign': 'Réattribuer',
  'admin.assign.title': 'Attribuer à un administrateur',
  'admin.assign.intro':
    "{{count}} boutique(s) seront confiées à l'administrateur choisi, qui deviendra le seul à les voir et à les valider. L'opération est auditée.",
  'admin.assign.adminLabel': 'Administrateur gestionnaire',
  'admin.assign.adminPlaceholder': 'Choisir un administrateur',
  'admin.assign.noAdmins': 'Aucun administrateur actif. Créez-en un dans « Administrateurs ».',
  'admin.assign.confirm': 'Attribuer',
  'admin.assign.success': 'Boutique(s) attribuée(s) : {{count}}.',
  'admin.portfolios.title': 'Portefeuilles',
  'admin.portfolios.subtitle':
    'Qui gère combien de marchands. Le superadmin attribue et transfère ; il ne valide jamais une boutique.',
  'admin.portfolios.reasonRequiredDescription':
    'Indiquez le motif de la consultation (10 caractères minimum) pour afficher les portefeuilles ; chaque lecture est auditée.',
  'admin.portfolios.loadError': 'Impossible de charger les portefeuilles.',
  'admin.portfolios.colAdmin': 'Administrateur',
  'admin.portfolios.colTotal': 'Marchands',
  'admin.portfolios.colPending': 'En attente',
  'admin.portfolios.colActive': 'Actifs',
  'admin.portfolios.colSuspended': 'Suspendus',
  'admin.portfolios.colRefused': 'Refusés',
  'admin.portfolios.disabledBadge': 'Compte désactivé',
  'admin.portfolios.unassignedRow': 'Boutiques non attribuées',
  'admin.portfolios.unassignedRowHint':
    "Visibles du superadmin seul. Personne ne peut les valider tant qu'elles ne sont pas attribuées.",
  'admin.portfolios.viewMerchants': 'Voir les marchands',
  'admin.portfolios.assignUnassigned': 'Attribuer…',
  'admin.portfolios.transfer': 'Transférer le portefeuille',
  'admin.portfolios.empty': 'Aucun administrateur.',
  'admin.transfer.title': 'Transférer le portefeuille de {{name}}',
  'admin.transfer.intro':
    "Les {{count}} marchand(s) de cet administrateur passent à l'administrateur choisi. Il ne pourra être désactivé ni privé de son rôle qu'une fois son portefeuille vide.",
  'admin.transfer.targetLabel': 'Administrateur destinataire',
  'admin.transfer.confirm': 'Transférer',
  'admin.transfer.success': 'Portefeuille transféré : {{count}} marchand(s).',
  'admin.organisations.managerLabel': 'Administrateur gestionnaire',
  'admin.organisations.managerHint':
    "Le superadmin n'est pas gestionnaire : choisissez l'administrateur qui gérera et validera ce marchand.",
  'admin.organisations.managerRequired': "Choisissez l'administrateur gestionnaire.",
  'admin.organisations.ownPortfolioNote':
    'Ce marchand sera rattaché à votre portefeuille : vous seul le verrez et pourrez le valider (un superadmin peut le réattribuer).',
  'admin.handover.title': 'Fiche de remise au marchand',
  'admin.handover.intro':
    'À transmettre au marchand. Le mot de passe provisoire, lui, se remet séparément : en main propre ou par un canal que vous jugez sûr.',
  'admin.handover.dashboardLabel': "Adresse de l'espace marchand",
  'admin.handover.dashboardMissing':
    'Adresse non configurée : communiquez-la au marchand (réglage VITE_MERCHANT_DASHBOARD_URL).',
  'admin.handover.accountLabel': 'Identifiant (adresse e-mail du compte)',
  'admin.handover.expiresLabel': "Mot de passe provisoire valable jusqu'au",
  'admin.handover.stepsTitle': 'Ce que le marchand doit faire',
  'admin.handover.step1':
    "Ouvrez l'adresse ci-dessus et connectez-vous avec votre adresse e-mail et le mot de passe provisoire.",
  'admin.handover.step2': 'Choisissez votre propre mot de passe (12 caractères minimum).',
  'admin.handover.step3':
    "Complétez votre boutique : le public la verra dès que la plateforme l'aura approuvée.",
  'admin.handover.copy': 'Copier les instructions',
  'admin.handover.copied': 'Instructions copiées (sans le mot de passe).',
  'admin.handover.copyFailed': 'La copie a échoué : recopiez les instructions à la main.',
  'admin.handover.noPassword': 'Cette fiche ne contient jamais le mot de passe.',
  'admin.organisations.localeLabel': 'Langue du compte',
  'admin.organisations.localeHint': 'Langue des écrans du marchand à sa première connexion.',
  'admin.merchants.action.reissueUnused': 'Nouveau mot de passe provisoire',
  'admin.merchants.reissueUnusedWarning':
    "Possible tant que le propriétaire ne s'est jamais connecté. Dès sa première connexion, seul un superadmin peut réinitialiser son mot de passe.",
  'admin.merchants.refuseModalTitle': 'Refuser cette boutique',
  'admin.merchants.suspendModalTitle': 'Suspendre cette boutique',
  'admin.merchants.refuseWarning':
    "Un refus est définitif : la boutique ne pourra plus être approuvée et le demandeur devra s'inscrire à nouveau.",
  'admin.merchants.suspendWarning': 'La vitrine publique de la boutique disparaît immédiatement.',
  'admin.merchants.resetOwnerTitle': 'Nouveau mot de passe du propriétaire',
  'admin.merchants.resetOwnerWarning':
    "Un mot de passe provisoire est généré et affiché une seule fois. Les sessions du propriétaire sont fermées et il devra choisir un nouveau mot de passe à sa prochaine connexion. Aucun e-mail n'est envoyé : vous lui remettez ce mot de passe vous-même.",
  'admin.merchants.resetOwnerGenerate': 'Générer le mot de passe',
  'admin.organisations.statusActive': 'Boutique approuvée.',
  'admin.organisations.statusPending': "Boutique en attente d'approbation.",
  'admin.organisations.createAnother': 'Créer un autre compte',
  'errors.reauthRequired': 'Confirmez votre mot de passe pour continuer.',
  'errors.reauthFailed':
    'Mot de passe incorrect. Après plusieurs erreurs, le compte est verrouillé quelques minutes.',
  'errors.emailUnavailable': 'Cette adresse ne peut pas être utilisée pour un nouveau compte.',
  'errors.accountBelongsToShop':
    'Ce compte appartient à une boutique : il ne peut pas recevoir un rôle plateforme.',
  'errors.ownerAmbiguous':
    'Cette boutique a plusieurs propriétaires : la réinitialisation passe par la procédure opérateur.',
  'errors.ownerDisabled': 'Le compte du propriétaire est désactivé.',
  'admin.organisations.submit': 'Créer le compte',
  'admin.organisations.success': 'Compte créé.',

  // ---- admin: analytics ----
  'admin.analytics.title': 'Analytics plateforme',
  'admin.analytics.subtitle':
    "Indicateurs réels, calculés sur l'ensemble des boutiques de la plateforme.",
  'admin.analytics.reasonPlaceholder': 'Ex. : revue hebdomadaire de la performance plateforme',
  'admin.analytics.loadError': 'Impossible de charger les analytics',
  'admin.analytics.gmv': 'GMV',
  'admin.analytics.ordersAllShops': 'Commandes (toutes boutiques)',
  'admin.analytics.activeMerchants': 'Marchands actifs',
  'admin.analytics.pendingMerchants': 'Marchands en attente',
  'admin.analytics.byStatusTitle': 'Marchands par statut',
  'admin.analytics.noMerchants': 'Aucun marchand enregistré',

  // ---- merchant: clients ----
  'merchant.clients.subtitle':
    "Reconstitué depuis vos commandes réelles (aucun endpoint de liste de clients n'existe encore) — un client correspond à un numéro de téléphone.",
  'merchant.clients.truncatedNotice': 'À savoir',
  'merchant.clients.truncatedDescription':
    'Cette liste est construite à partir de vos 2 000 commandes les plus récentes. Au-delà, un client plus ancien peut manquer.',
  'merchant.clients.loadError': 'Impossible de charger les clients',
  'merchant.clients.emptyTitle': 'Aucun client pour le moment',
  'merchant.clients.emptyDescription': 'Vos clients apparaîtront ici dès votre première commande.',
  'merchant.clients.table.colClient': 'Client',
  'merchant.clients.table.colLastActivity': 'Dernière activité',
  'merchant.clients.table.colTotalSpent': 'Total dépensé',
  'merchant.clients.viewDetail': 'Voir détail',
  'merchant.clients.orderCountLabel': 'Commande(s)',
  'merchant.clients.orderHistoryTitle': 'Historique des commandes',
  'merchant.clients.purchasedProductsTitle': 'Produits achetés',
  'merchant.clients.noNonCancelledProducts': 'Aucun produit sur une commande non annulée.',
  'merchant.clients.couponsUsedTitle': 'Coupons utilisés',
  'merchant.clients.noCouponUsed': 'Aucun coupon utilisé',
  'merchant.clients.noCouponUsedDescription':
    "Ce client n'a encore utilisé aucun coupon de la boutique.",
  'merchant.clients.couponOn': 'sur',

  // ---- merchant: marketing ----
  'merchant.marketing.subtitlePrefix':
    'Performance réelle de vos coupons. Pour créer ou modifier un coupon, rendez-vous sur',
  'merchant.marketing.loadError': 'Impossible de charger les données marketing',
  'merchant.marketing.activeCoupons': 'Coupons actifs',
  'merchant.marketing.activeCouponsHint': 'Sur {{total}} coupon(s) au total',
  'merchant.marketing.totalUsage': 'Utilisations totales',
  'merchant.marketing.couponsCreated': 'Coupons créés',
  'merchant.marketing.performanceTitle': 'Performance des promotions',
  'merchant.marketing.emptyTitle': 'Aucun coupon pour le moment',
  'merchant.marketing.emptyDescription':
    'Créez un coupon depuis Promotions pour voir sa performance ici.',
  'merchant.marketing.table.colCode': 'Code',
  'merchant.marketing.table.colValue': 'Valeur',
  'merchant.marketing.table.colUsageRate': "Taux d'utilisation",
  'merchant.marketing.usageUnlimited': '{{count}} (illimité)',
  'merchant.marketing.campaignsTitle': 'Campagnes',
  'merchant.marketing.campaignsDescription':
    "Fonctionnalité non encore disponible. Aucun module de campagnes (email, SMS) n'existe actuellement dans la plateforme — seuls les coupons ci-dessus sont réels et exploitables aujourd'hui.",

  // ---- merchant: payments ----
  'merchant.payments.subtitle':
    "Paiement à la livraison uniquement. Cette vue est calculée depuis le statut de vos commandes — aucune donnée de paiement séparée n'est encore exposée à cet écran.",
  'merchant.payments.loadError': 'Impossible de charger les paiements',
  'merchant.payments.truncatedDescription':
    'Ces chiffres portent sur vos 2 000 commandes les plus récentes.',
  'merchant.payments.amountCollected': 'Montant encaissé',
  'merchant.payments.amountCollectedHint': 'Commandes au paiement confirmé',
  'merchant.payments.pendingKpi': 'En attente',
  'merchant.payments.confirmedKpi': 'Confirmés',
  'merchant.payments.failedKpi': 'Échoués',
  'merchant.payments.filterAria': 'Filtrer par statut de paiement',
  'merchant.payments.emptyForStatus': 'Aucune commande pour ce statut',
  'merchant.payments.table.colPayment': 'Paiement',
  'merchant.payments.bucket.pending': 'En attente',
  'merchant.payments.bucket.confirmed': 'Confirmé',
  'merchant.payments.bucket.failed': 'Échoué',
  'merchant.payments.bucket.refunded': 'Remboursé',
  'merchant.payments.bucket.cancelled': 'Annulé',

  // ---- merchant: delivery ----
  'merchant.delivery.subtitle':
    "Commandes en attente d'expédition, avec leur historique de tentatives.",
  'merchant.delivery.loadError': 'Impossible de charger les expéditions',
  'merchant.delivery.emptyTitle': 'Aucune expédition en attente',
  'merchant.delivery.emptyDescription':
    "Les commandes marquées « Emballée » apparaîtront ici si leur envoi n'a pas encore réussi.",
  'merchant.delivery.tableCaption': 'Expéditions en attente',
  'merchant.delivery.table.colShipment': 'Expédition',
  'merchant.delivery.table.colAttempts': "Tentatives d'envoi",
  'merchant.delivery.table.colDetail': 'Détail',
  'merchant.delivery.table.colLastAttempt': 'Dernière tentative',
  'merchant.delivery.noDetail': 'Aucun détail pour le moment.',
  'merchant.delivery.status.pendingDispatch': "En attente d'expédition",
  'merchant.delivery.status.dispatchFailed': "Échec d'expédition",
  // ---- Expéditions (Jibex) ----
  'merchant.ship.title': 'Expéditions',
  'merchant.ship.subtitle':
    'Vos colis chez Jibex : leur statut, suivi automatiquement, et ce que vous pouvez faire.',
  'merchant.ship.noAccount':
    'Aucun compte Jibex n’est connecté : les commandes « Préparées » ne sont pas envoyées.',
  'merchant.ship.noAccountLink': 'Connecter Jibex',
  'merchant.ship.tab.in_progress': 'En cours',
  'merchant.ship.tab.to_send': 'À envoyer',
  'merchant.ship.tab.failed': 'Échecs d’envoi',
  'merchant.ship.tab.delivered': 'Livrées',
  'merchant.ship.tab.returns': 'Retours et annulations',
  'merchant.ship.tab.attention': 'À traiter',
  'merchant.ship.tableCaption': 'Expéditions',
  'merchant.ship.col.order': 'Commande',
  'merchant.ship.col.recipient': 'Destinataire',
  'merchant.ship.col.status': 'Statut',
  'merchant.ship.col.tracking': 'N° de suivi',
  'merchant.ship.col.updated': 'Mis à jour',
  'merchant.ship.col.open': 'Détail',
  'merchant.ship.empty.title': 'Aucune expédition ici',
  'merchant.ship.empty.description':
    'Les commandes passées à « Préparée » sont envoyées à Jibex et apparaissent ici.',
  'merchant.ship.status.pending_dispatch': 'À envoyer',
  'merchant.ship.status.dispatch_failed': 'Envoi échoué',
  'merchant.ship.status.registered': 'Enregistré chez Jibex',
  'merchant.ship.status.pickup_scheduled': 'Enlèvement prévu',
  'merchant.ship.status.in_transit': 'En transit',
  'merchant.ship.status.out_for_delivery': 'En cours de livraison',
  'merchant.ship.status.delivered': 'Livré',
  'merchant.ship.status.return_in_progress': 'Retour en cours',
  'merchant.ship.status.returned': 'Retourné',
  'merchant.ship.status.under_review': 'En vérification',
  'merchant.ship.status.cancelled': 'Annulé',
  'merchant.ship.status.lost': 'Perdu',
  'merchant.ship.status.unknown': 'Statut inconnu',
  'merchant.ship.attention.under_review': 'Jibex vérifie ce colis',
  'merchant.ship.attention.cancelled': 'Annulé par Jibex : à vous de décider',
  'merchant.ship.attention.lost': 'Colis perdu : contactez Jibex',
  'merchant.ship.attention.unknown_status': 'Statut Jibex inconnu',
  'merchant.ship.attention.unexpected_transition': 'Évolution inattendue',
  'merchant.ship.attention.credentials': 'Identifiants Jibex refusés',
  'merchant.ship.attention.return': 'Retour à surveiller',
  'merchant.ship.attention.cancellation_refused': 'Annulation refusée par Jibex',
  'merchant.ship.source.carrier_poll': 'suivi Jibex',
  'merchant.ship.source.carrier_notification': 'notification Jibex',
  'merchant.ship.source.create_response': 'création du colis',
  'merchant.ship.source.system': 'Souk',
  'merchant.ship.source.operator': 'votre action',
  'merchant.ship.detail.title': 'Expédition',
  'merchant.ship.detail.order': 'Commande',
  'merchant.ship.detail.recipient': 'Destinataire',
  'merchant.ship.detail.tracking': 'N° de suivi',
  'merchant.ship.detail.copy': 'Copier',
  'merchant.ship.detail.copied': 'Copié',
  'merchant.ship.detail.reference': 'Référence chez Jibex',
  'merchant.ship.detail.carrierStatus': 'Statut Jibex',
  'merchant.ship.detail.reason': 'Motif donné par Jibex',
  'merchant.ship.detail.cancellation': 'Annulation',
  'merchant.ship.detail.cancellationRequested': 'demandée, en attente de la décision de Jibex',
  'merchant.ship.detail.cancellationRefused': 'refusée par Jibex',
  'merchant.ship.detail.lastError': 'Pourquoi l’envoi a échoué',
  'merchant.ship.detail.nextPoll': 'Prochaine vérification',
  'merchant.ship.detail.timeline': 'Historique',
  'merchant.ship.detail.noEvents': 'Pas encore d’évènement.',
  'merchant.ship.detail.amounts': 'Montants',
  'merchant.ship.detail.amountsNotice':
    'Tels que transmis par Jibex. Leur sens exact (frais déduits ou non) n’est pas encore confirmé par Jibex : Souk ne s’en sert pour aucun calcul, et la commande et la facture restent inchangées.',
  'merchant.ship.detail.codSent': 'Montant à encaisser envoyé',
  'merchant.ship.detail.journal': 'Journal des appels à Jibex',
  'merchant.ship.action.open': 'Ouvrir',
  'merchant.ship.action.refresh': 'Actualiser',
  'merchant.ship.action.retry': 'Réessayer l’envoi',
  'merchant.ship.action.cancel': 'Annuler le colis',
  'merchant.ship.action.resend': 'Renvoyer la commande',
  'merchant.ship.done.refresh': 'Statut actualisé.',
  'merchant.ship.auto.note': 'Statut actualisé automatiquement depuis Jibex toutes les 60 s.',
  'merchant.ship.auto.last': 'Dernière actualisation automatique : {{time}} (toutes les 60 s).',
  'merchant.sendJibex.title': 'Confirmer et envoyer à Jibex',
  'merchant.sendJibex.action': 'Envoyer à Jibex',
  'merchant.sendJibex.orderLine': 'Commande #{{number}} · {{total}}',
  'merchant.sendJibex.recipient': 'Destinataire',
  'merchant.sendJibex.parcel': 'Informations du colis',
  'merchant.sendJibex.phone': 'Tél',
  'merchant.sendJibex.phone2': 'Tél 2',
  'merchant.sendJibex.name': 'Nom',
  'merchant.sendJibex.address': 'Adresse',
  'merchant.sendJibex.addressPlaceholder': 'Rue, quartier, numéro…',
  'merchant.sendJibex.governorate': 'Gouvernorat',
  'merchant.sendJibex.delegation': 'Délégation',
  'merchant.sendJibex.delegationPlaceholder': 'Ex. Bab Bhar',
  'merchant.sendJibex.comment': 'Commentaire',
  'merchant.sendJibex.commentHint': 'Transmis à Jibex avec le colis (avec le Tél 2).',
  'merchant.sendJibex.price': 'Prix (TND)',
  'merchant.sendJibex.priceHint': 'Montant de la commande, encaissé à la livraison.',
  'merchant.sendJibex.pieces': 'NB Pièces',
  'merchant.sendJibex.designation': 'Désignation',
  'merchant.sendJibex.designationHint': 'Articles de la commande, transmis à Jibex.',
  'merchant.sendJibex.weight': 'Poids (Kg)',
  'merchant.sendJibex.exchange': 'Échange',
  'merchant.sendJibex.type': 'Type',
  'merchant.sendJibex.typeFIX': 'FIX - Fixe',
  'merchant.sendJibex.fragile': 'Fragile',
  'merchant.sendJibex.opening': 'Ouverture',
  'merchant.sendJibex.yes': 'Oui',
  'merchant.sendJibex.no': 'Non',
  'merchant.sendJibex.select': 'Sélectionner…',
  'merchant.sendJibex.required': 'Champ obligatoire.',
  'merchant.sendJibex.missingTitle':
    'Des informations manquent pour créer le colis : complétez les champs signalés.',
  'merchant.sendJibex.missingField': 'À compléter',
  'merchant.sendJibex.phoneInvalid': '8 chiffres, avec ou sans +216.',
  'merchant.sendJibex.weightInvalid': 'Un poids supérieur à 0 kg.',
  'merchant.sendJibex.piecesInvalid': 'Un nombre entier, au moins 1.',
  'merchant.sendJibex.submit': 'Créer et envoyer',
  'merchant.sendJibex.sending': 'Envoi à Jibex en cours…',
  'merchant.sendJibex.success': 'Colis créé chez Jibex : {{tracking}}.',
  'merchant.sendJibex.failed':
    'Jibex n’a pas créé le colis : {{reason}} Corrigez puis renvoyez — vos saisies sont conservées.',
  'merchant.sendJibex.stillSending': 'L’envoi continue en arrière-plan : suivez-le dans Livraison.',
  'merchant.sendJibex.alreadySent': 'Cette commande est déjà chez Jibex ({{tracking}}).',
  'merchant.sendJibex.inProgress': 'Un envoi de cette commande est déjà en cours.',
  'merchant.sendJibex.notSendable': 'Cette commande ne peut pas être envoyée dans son état actuel.',
  'merchant.sendJibex.noCarrier':
    'Aucun compte Jibex connecté : la commande sera seulement marquée « Préparée », sans envoi.',
  'merchant.sendJibex.markPacked': 'Marquer préparée',
  'merchant.sendJibex.openDelivery': 'Voir dans Livraison',
  'merchant.sendJibex.phone2Invalid': 'Tél 2 : 8 chiffres, avec ou sans +216.',
  'merchant.ship.done.retry': 'Envoi relancé : le statut se met à jour dans un instant.',
  'merchant.ship.done.cancel': 'Colis annulé chez Jibex. La commande est de nouveau « Préparée ».',
  'merchant.ship.done.resend': 'Commande renvoyée : un nouveau colis va être créé.',
  'merchant.ship.cancel.title': 'Annuler ce colis chez Jibex ?',
  'merchant.ship.cancel.body': 'Possible seulement tant que Jibex n’a pas pris le colis en charge.',
  'merchant.ship.cancel.effect':
    'La commande redeviendra « Préparée » : vous pourrez la corriger puis la renvoyer. Elle n’est pas renvoyée automatiquement.',
  'merchant.ship.cancel.keep': 'Retour',
  'merchant.ship.cancel.confirm': 'Annuler le colis',
  'merchant.ship.city.label': 'Ville envoyée à Jibex',
  'merchant.ship.city.hint':
    'Format « Gouvernorat, Délégation » (ex. Tunis, Bab Bhar). L’adresse de la commande n’est pas modifiée.',
  'merchant.ship.city.save': 'Enregistrer la ville',
  'merchant.ship.city.reset': 'Revenir à l’adresse de la commande',
  'merchant.ship.city.saved': 'Ville enregistrée. Réessayez l’envoi pour l’utiliser.',
  'merchant.ship.lastError.INVALID_PHONE':
    'Le téléphone du client n’est pas un numéro tunisien à 8 chiffres.',
  'merchant.ship.lastError.INVALID_CITY':
    'La ville n’est pas acceptée : corrigez-la ci-dessous puis réessayez.',
  'merchant.ship.lastError.INVALID_ADDRESS': 'L’adresse est vide ou trop longue.',
  'merchant.ship.lastError.INVALID_NAME': 'Le nom du destinataire est vide ou trop long.',
  'merchant.ship.lastError.INVALID_REQUEST':
    'Jibex a refusé les informations du colis (souvent la ville) : vérifiez-les, corrigez la ville si besoin, puis réessayez.',
  'merchant.ship.lastError.credentials':
    'Jibex refuse les identifiants du compte : vérifiez-les dans « Compte Jibex ».',
  'merchant.ship.lastError.FORBIDDEN':
    'Votre clé Jibex n’a pas le droit nécessaire : demandez-le à Jibex.',
  'merchant.ship.lastError.ECOMMERCE_STORE_NOT_FOUND':
    'L’identifiant de boutique Jibex n’appartient pas à votre compte.',
  'merchant.ship.lastError.CARRIER_NOT_CONFIGURED':
    'La plateforme n’est pas encore reliée à Jibex.',
  'merchant.ship.lastError.other': 'Jibex a refusé l’envoi (code {{code}}).',
  'merchant.ship.error.notFound': 'Expédition introuvable.',
  'merchant.ship.error.notRetryable':
    'Cet envoi ne peut pas être relancé (il n’a pas échoué, ou la commande n’est plus « Préparée »).',
  'merchant.ship.error.notAtCarrier': 'Ce colis n’est pas encore chez Jibex.',
  'merchant.ship.error.notCancellable':
    'Ce colis ne peut plus être annulé : Jibex l’a déjà pris en charge.',
  'merchant.ship.error.notResendable':
    'Cette commande ne peut pas être renvoyée (le colis n’est pas annulé, ou la commande n’est plus « Préparée »).',
  'merchant.ship.error.alreadyAtCarrier':
    'Le colis est déjà chez Jibex : la ville ne peut plus être modifiée.',
  'merchant.ship.error.invalidCity': 'Ville invalide : 1 à 120 caractères.',
  'merchant.ship.error.accountUnavailable':
    'Aucun compte Jibex utilisable : connectez-le dans « Compte Jibex ».',
  'merchant.ship.error.credentialsRefused': 'Jibex refuse les identifiants de votre compte.',
  'merchant.ship.error.scopeMissing':
    'Votre clé Jibex n’a pas le droit nécessaire pour cette action : demandez-le à Jibex.',
  'merchant.ship.error.carrierRateLimited': 'Jibex demande de patienter un peu avant de réessayer.',
  'merchant.ship.error.carrierUnavailable':
    'Jibex ne répond pas pour le moment. Réessayez dans quelques minutes.',
  'merchant.ship.error.carrierRefused': 'Jibex a refusé cette demande.',
  'merchant.delivery.notifications.title': 'Notifications Jibex',
  'merchant.delivery.notifications.description':
    'Jibex prévient Souk à chaque changement de statut d’un colis : vos commandes se mettent à jour aussitôt. Sans notifications, Souk vérifie régulièrement chez Jibex.',
  'merchant.delivery.notifications.unavailable':
    'Ce serveur n’a pas d’adresse publique pour recevoir les notifications. Le suivi se fait par vérification régulière.',
  'merchant.delivery.notifications.needAccount': 'Connectez d’abord votre compte Jibex.',
  'merchant.delivery.notifications.notConfigured': 'Pas encore activées.',
  'merchant.delivery.notifications.configured': 'Activées. Adresse à donner à Jibex :',
  'merchant.delivery.notifications.generate': 'Créer l’adresse et le secret',
  'merchant.delivery.notifications.rotate': 'Changer le secret',
  'merchant.delivery.notifications.rotateConfirm':
    'L’ancien secret cessera de fonctionner immédiatement : Jibex devra recevoir le nouveau.',
  'merchant.delivery.notifications.disable': 'Désactiver',
  'merchant.delivery.notifications.revealTitle': 'Adresse et secret de signature',
  'merchant.delivery.notifications.secretOnce':
    'Le secret n’est affiché qu’une seule fois. Copiez-le maintenant.',
  'merchant.delivery.notifications.steps':
    'Transmettez cette adresse et ce secret à la société qui livre (Jibex), qui les enregistre dans votre fiche expéditeur.',
  'merchant.delivery.notifications.urlLabel': 'Adresse de notification',
  'merchant.delivery.notifications.secretLabel': 'Secret de signature',
  'merchant.delivery.notifications.done': 'J’ai copié',
  'merchant.delivery.notifications.errorUnavailable':
    'Les notifications ne peuvent pas être activées sur ce serveur.',

  // ---- auth pages (Lot E3) ----
  'auth.email.label': 'Adresse email',
  'auth.password.label': 'Mot de passe',
  'auth.confirmPassword.label': 'Confirmer le mot de passe',
  'auth.password.hint': '8 caractères minimum.',
  'auth.validation.email': 'Cette adresse email ne semble pas valide.',
  'auth.validation.passwordShort': 'Le mot de passe doit contenir au moins 8 caractères.',
  'auth.validation.chosenPasswordShort': 'Le mot de passe doit contenir au moins 12 caractères.',
  'auth.validation.passwordsMismatch': 'Les deux mots de passe ne sont pas identiques.',
  'auth.login.title': 'Connexion',
  'auth.login.subtitle': 'Retrouvez votre espace marchand.',
  'auth.login.submit': 'Se connecter',
  'auth.login.forgot': 'Mot de passe oublié ?',
  'auth.login.platformAccountNotice':
    'Vous avez reçu un mot de passe provisoire de la plateforme ? Saisissez-le ici : vous serez invité à en choisir un nouveau. Il est valable 72 heures.',
  'auth.login.invalid':
    'Adresse email ou mot de passe incorrect. Si vous avez essayé plusieurs fois, patientez un instant avant de réessayer. Mot de passe provisoire perdu ou expiré (72 h) ? Contactez le support de la plateforme.',
  'auth.forgot.title': 'Mot de passe oublié',
  'auth.forgot.subtitle':
    'Indiquez votre adresse : nous vous envoyons un lien pour en choisir un nouveau.',
  'auth.forgot.submit': 'Envoyer le lien',
  'auth.forgot.back': 'Retour à la connexion',
  'auth.forgot.sentTitle': 'Vérifiez votre boîte email',
  'auth.forgot.sentSubtitle':
    "Si un compte existe avec cette adresse, un lien vient d'être envoyé.",
  'auth.forgot.sentHint':
    'Le lien expire après un court délai. Pensez à vérifier vos courriers indésirables.',
  'auth.reset.title': 'Nouveau mot de passe',
  'auth.reset.subtitle': "Choisissez un mot de passe que vous n'utilisez nulle part ailleurs.",
  'auth.reset.newPassword': 'Nouveau mot de passe',
  'auth.reset.submit': 'Enregistrer le mot de passe',
  'auth.reset.invalidTitle': 'Lien incomplet',
  'auth.reset.invalidSubtitle': 'Ce lien semble avoir été coupé en route.',
  'auth.reset.invalidHint':
    'Vérifiez que vous avez copié le lien en entier, ou demandez-en un nouveau.',
  'auth.reset.requestNew': 'Demander un nouveau lien',
  'auth.reset.failed':
    "Ce lien n'est plus valable : il a expiré ou a déjà servi. Demandez-en un nouveau.",
  'auth.reset.doneTitle': 'Mot de passe mis à jour',
  'auth.reset.doneSubtitle': 'Vous pouvez vous connecter avec votre nouveau mot de passe.',
  'auth.reset.doneHint': 'Par sécurité, vos autres sessions ont été déconnectées.',
  'auth.reset.signIn': 'Se connecter',
  'auth.changePassword.title': 'Choisissez votre mot de passe',
  'auth.changePassword.subtitle':
    "Le mot de passe provisoire qui vous a été remis ne sert qu'à cette étape. Choisissez-en un que vous seul connaissez.",
  'auth.changePassword.currentLabel': 'Mot de passe provisoire',
  'auth.changePassword.signOut': 'Se déconnecter',
  'auth.login.noAccountFooter':
    "Vous n'avez pas encore de compte ? La plateforme crée les comptes marchands : contactez votre interlocuteur.",
  'auth.changePassword.welcomeTitle': 'Bienvenue — choisissez votre mot de passe',
  'auth.changePassword.welcomeSubtitle':
    "La plateforme a créé votre compte marchand. Le mot de passe provisoire qui vous a été remis ne sert qu'à cette étape : choisissez-en un que vous seul connaissez.",
  'auth.changePassword.rule': 'Au moins 12 caractères, différent du mot de passe provisoire.',
  'auth.changePassword.step1': 'Connexion',
  'auth.changePassword.step2': 'Choisir mon mot de passe',
  'auth.changePassword.step3': 'Accéder à mon espace',
  'auth.changePassword.steps': 'Étapes',
  'auth.changePassword.provisionalWrong':
    'Le mot de passe provisoire est incorrect. Saisissez-le exactement comme il vous a été remis. Après plusieurs essais, le compte est verrouillé temporairement.',
  'auth.forgot.noEmailSubtitle': "Cette plateforme n'envoie pas encore d'e-mails.",
  'auth.forgot.noEmailBody':
    "Pour retrouver l'accès à votre compte, contactez le support de la plateforme : un nouveau mot de passe provisoire vous sera remis en main propre.",
  'merchant.welcome.title': 'Bienvenue.',
  'merchant.welcome.subtitle': 'Votre compte a été créé par la plateforme.',
  'merchant.welcome.passwordSaved': 'Mot de passe enregistré.',
  'merchant.welcome.statusPending':
    "Votre boutique est en attente d'approbation : elle ne sera pas visible du public tant que la plateforme ne l'a pas approuvée. En attendant, vous pouvez la préparer.",
  'merchant.welcome.statusActive':
    "Votre boutique est approuvée. Complétez-la pour qu'elle soit prête à recevoir ses premiers clients.",
  'merchant.welcome.cta': 'Compléter ma boutique',
  'merchant.welcome.dismiss': 'Fermer',

  // ---- merchant: Jibex account (Lot L5a) ----
  'merchant.delivery.account.title': 'Compte Jibex',
  'merchant.delivery.account.subtitle':
    "Reliez votre propre compte Jibex : vos expéditions partent avec vos identifiants, jamais ceux d'un autre marchand.",
  'merchant.delivery.account.back': 'Retour aux expéditions',
  'merchant.delivery.account.linkFromDelivery': 'Compte Jibex',
  'merchant.delivery.account.ownerOnly':
    'Seul le propriétaire de la boutique peut gérer le compte Jibex.',
  'merchant.delivery.account.loadError': 'Impossible de charger le compte Jibex',
  'merchant.delivery.account.platformNotReady':
    "La connexion à Jibex n'est pas encore activée sur la plateforme. Vous pouvez enregistrer vos identifiants ; le test de connexion sera possible après activation.",
  'merchant.delivery.account.state.not_configured': 'Non configuré',
  'merchant.delivery.account.state.untested': 'À tester',
  'merchant.delivery.account.state.connected': 'Connecté',
  'merchant.delivery.account.state.invalid': 'Identifiants invalides',
  'merchant.delivery.account.state.misconfigured': 'Configuration à corriger',
  'merchant.delivery.account.state.network_error': 'Erreur réseau',
  'merchant.delivery.account.stateText.not_configured':
    "Aucun compte Jibex n'est enregistré. Saisissez votre identifiant client et votre secret ci-dessous.",
  'merchant.delivery.account.stateText.untested':
    'Identifiants enregistrés, mais pas encore vérifiés. Lancez un test de connexion.',
  'merchant.delivery.account.stateText.connected': 'Jibex a accepté vos identifiants.',
  'merchant.delivery.account.stateText.invalid':
    "Jibex a refusé vos identifiants. Vérifiez l'identifiant client et le secret, puis enregistrez-les à nouveau.",
  'merchant.delivery.account.stateText.misconfigured':
    "Jibex a accepté vos identifiants, mais le compte n'est pas utilisable en l'état. Vérifiez l'identifiant de boutique ou contactez Jibex.",
  'merchant.delivery.account.stateText.network_error':
    "La dernière vérification n'a pas pu aboutir. Vos identifiants n'ont pas été jugés invalides : réessayez dans un instant.",
  'merchant.delivery.account.problem.invalidClient':
    'Identifiant client ou secret refusé par Jibex.',
  'merchant.delivery.account.problem.clientInactive': 'Ce compte client est désactivé chez Jibex.',
  'merchant.delivery.account.problem.forbidden': "Jibex a refusé l'accès pour ce compte.",
  'merchant.delivery.account.problem.storeNotFound':
    "La boutique Jibex indiquée est introuvable. Vérifiez l'identifiant de boutique.",
  'merchant.delivery.account.problem.rateLimited':
    'Jibex limite temporairement les requêtes. Réessayez dans quelques instants.',
  'merchant.delivery.account.problem.timeout': "Jibex n'a pas répondu à temps.",
  'merchant.delivery.account.problem.unreachable':
    'Jibex est injoignable ou a répondu de façon inattendue.',
  'merchant.delivery.account.problem.notActivated':
    "La connexion à Jibex n'est pas activée sur la plateforme.",
  'merchant.delivery.account.problem.secretUnreadable':
    'Le secret enregistré est illisible. Saisissez-le à nouveau.',
  'merchant.delivery.account.problem.unknown': 'Erreur inattendue (code {{code}}).',
  'merchant.delivery.account.form.title': 'Identifiants Jibex',
  'merchant.delivery.account.form.clientIdLabel': 'Identifiant client',
  'merchant.delivery.account.form.clientIdHint': "Fourni par Jibex. Ce n'est pas un secret.",
  'merchant.delivery.account.form.secretLabel': 'Secret Jibex',
  'merchant.delivery.account.form.secretHintNew':
    'Collez le secret fourni par Jibex. Il sera chiffré immédiatement et ne sera plus jamais affiché.',
  'merchant.delivery.account.form.secretHintKeep':
    'Un secret est enregistré. Laissez ce champ vide pour le conserver, ou saisissez-en un nouveau pour le remplacer.',
  'merchant.delivery.account.form.secretStored': 'Secret enregistré (chiffré)',
  'merchant.delivery.account.form.storeIdLabel': 'Identifiant de boutique Jibex',
  'merchant.delivery.account.form.storeIdHint':
    'Facultatif. À renseigner seulement si Jibex vous a donné un identifiant de boutique.',
  'merchant.delivery.account.form.save': 'Enregistrer',
  'merchant.delivery.account.form.saved': 'Identifiants enregistrés. Lancez un test de connexion.',
  'merchant.delivery.account.form.clientIdRequired': "L'identifiant client est obligatoire.",
  'merchant.delivery.account.form.clientIdTooLong':
    "L'identifiant client est trop long (200 caractères maximum).",
  'merchant.delivery.account.form.secretRequired':
    'Le secret est obligatoire pour enregistrer un nouvel identifiant.',
  'merchant.delivery.account.form.secretTooLong':
    'Le secret est trop long (512 caractères maximum).',
  'merchant.delivery.account.form.charactersNotAllowed':
    'Seuls les caractères imprimables standard sont acceptés (ni retours à la ligne, ni accents).',
  'merchant.delivery.account.form.storeIdInvalid':
    "L'identifiant de boutique doit être un nombre entier positif.",
  'merchant.delivery.account.form.unsavedHint':
    'Enregistrez vos modifications avant de tester la connexion.',
  'merchant.delivery.account.security.title': 'Sécurité du secret',
  'merchant.delivery.account.security.body':
    "Le secret est chiffré dès sa saisie. Il n'est jamais affiché, jamais renvoyé par l'application et jamais exporté. Pour le changer, saisissez-en un nouveau.",
  'merchant.delivery.account.status.title': 'Statut du compte',
  'merchant.delivery.account.status.lastCheck': 'Dernière vérification : {{date}}',
  'merchant.delivery.account.status.neverChecked': 'Jamais vérifié',
  'merchant.delivery.account.status.clientId': 'Identifiant client : {{clientId}}',
  'merchant.delivery.account.test.button': 'Tester la connexion',
  'merchant.delivery.account.test.running': 'Test en cours…',
  'merchant.delivery.account.test.done': 'Test terminé : {{result}}',
  'merchant.delivery.account.test.tooMany': 'Trop de tests. Réessayez dans {{seconds}} s.',
  'merchant.delivery.account.test.unavailable':
    'Le service est momentanément indisponible. Réessayez dans quelques instants.',
  'merchant.delivery.account.test.saveFirst': "Enregistrez d'abord vos identifiants.",
  'merchant.delivery.account.history.title': 'Historique des vérifications',
  'merchant.delivery.account.history.empty': 'Aucune vérification pour le moment.',
  'merchant.delivery.account.history.loadError': "Impossible de charger l'historique",
  'merchant.delivery.account.history.caption': 'Historique des vérifications du compte Jibex',
  'merchant.delivery.account.history.colDate': 'Date',
  'merchant.delivery.account.history.colResult': 'Résultat',
  'merchant.delivery.account.history.colDetail': 'Détail',
  'merchant.delivery.account.history.colClient': 'Identifiant client',
  'merchant.delivery.account.history.colDuration': 'Durée',
  'merchant.delivery.account.history.durationMs': '{{ms}} ms',
  'merchant.delivery.account.outcome.credentials_saved': 'Identifiants enregistrés',
  'merchant.delivery.account.outcome.connected': 'Connecté',
  'merchant.delivery.account.outcome.invalid_credentials': 'Identifiants refusés',
  'merchant.delivery.account.outcome.misconfigured': 'Configuration à corriger',
  'merchant.delivery.account.outcome.network_error': 'Erreur réseau',
  'merchant.delivery.account.outcome.rate_limited': 'Limite de requêtes',
  'merchant.delivery.account.outcome.unavailable': 'Service non activé',
  'merchant.delivery.account.outcome.disconnected': 'Compte déconnecté',

  // ---- merchant: invoices ----
  'merchant.invoices.subtitle':
    'Une facture est générée automatiquement pour chaque commande confirmée. Les mentions fiscales (TVA, conformité légale) restent désactivées en attendant leur validation.',
  'merchant.invoices.loadError': 'Impossible de charger les factures',
  'merchant.invoices.emptyTitle': 'Aucune facture pour le moment',
  'merchant.invoices.emptyDescription':
    'Les factures apparaîtront ici dès la première commande confirmée.',
  'merchant.invoices.downloadFailed': 'Téléchargement impossible.',
  'merchant.invoices.table.colInvoice': 'Facture',
  'merchant.invoices.table.colAmount': 'Montant',
  'merchant.invoices.fiscal': 'Fiscale',
  'merchant.invoices.nonFiscal': 'Non fiscale',
  'merchant.invoices.downloadPdf': 'Télécharger le PDF',
  'merchant.invoices.invoiceNumberPrefix': 'n°',

  // ---- merchant: staff ----
  'merchant.staff.inviteSuccess':
    'Invitation envoyée si un compte existe avec cette adresse. Vérifiez la liste ci-dessous.',
  'merchant.staff.ownerOnlyTitle': 'Accès réservé au propriétaire',
  'merchant.staff.ownerOnlyDescription': "Seul le propriétaire de la boutique peut gérer l'équipe.",
  'merchant.staff.subtitle': 'Invitez des membres existants de Souk et gérez leurs permissions.',
  'merchant.staff.inviteMember': 'Inviter un membre',
  'merchant.staff.loadError': "Impossible de charger l'équipe",
  'merchant.staff.emptyTitle': 'Aucun membre pour le moment',
  'merchant.staff.inviteAction': 'Inviter',
  'merchant.staff.createAccount': 'Créer un compte',
  'merchant.staff.createAccountTitle': "Créer le compte d'un membre",
  'merchant.staff.createAccountIntro':
    "Un mot de passe provisoire est généré pour ce membre. Vous le verrez une seule fois ; le membre devra le remplacer à sa première connexion. Aucun e-mail n'est envoyé.",
  'merchant.staff.createAccountSubmit': 'Créer le compte',
  'merchant.staff.accountCreatedTitle': 'Compte créé',
  'merchant.staff.reissue': 'Nouveau mot de passe',
  'merchant.staff.reissueTitle': 'Nouveau mot de passe provisoire',
  'merchant.staff.reissueIntro':
    "Le mot de passe actuel de ce membre cessera de fonctionner et il sera déconnecté partout. Indiquez le motif : il est conservé dans le journal d'audit.",
  'merchant.staff.reissueReason': 'Motif (10 caractères minimum)',
  'merchant.staff.reissueSubmit': 'Générer un nouveau mot de passe',
  'merchant.staff.managedBadge': 'Compte créé par la boutique',
  'merchant.staff.table.colMember': 'Membre',
  'merchant.staff.table.colRole': 'Rôle',
  'merchant.staff.roleOwner': 'Propriétaire',
  'merchant.staff.roleStaff': 'Personnel',
  'merchant.staff.statusSuspended': 'Suspendu',
  'merchant.staff.fullAccess': 'Accès complet',
  'merchant.staff.noPermissions': 'Aucune',
  'merchant.staff.suspendAction': 'Suspendre',
  'merchant.staff.reactivateAction': 'Réactiver',
  'merchant.staff.removeAction': 'Retirer',
  'merchant.staff.removeConfirm': "Retirer {{email}} de l'équipe ?",
  'merchant.staff.suspendedToast': 'Membre suspendu.',
  'merchant.staff.reactivatedToast': 'Membre réactivé.',
  'merchant.staff.removedToast': 'Membre retiré.',
  'merchant.staff.permissionsUpdatedToast': 'Permissions mises à jour.',
  'merchant.staff.table.colPermissions': 'Permissions',
  'merchant.staff.form.emailLabel': 'Adresse email',
  'merchant.staff.form.hint':
    "L'invitation ne fonctionne que pour une adresse ayant déjà un compte Souk. Vous ne recevrez pas de confirmation directe — vérifiez la liste ci-dessous après l'envoi.",
  'merchant.staff.permission.catalog': 'Catalogue (produits, catégories)',
  'merchant.staff.permission.analytics': 'Analytique (lecture)',

  // ---- merchant: settings ----
  'merchant.settings.subtitle':
    'Gérez votre boutique, votre équipe et vos préférences depuis un seul endroit.',
  'merchant.settings.storeCardDescription':
    'Profil public, horaires, politiques, référencement et santé de la boutique.',
  'merchant.settings.teamCardDescription':
    'Invitations, rôles et accès des membres de votre équipe.',

  // ---- merchant: analytics ----
  'merchant.analytics.subtitle':
    'Indicateurs réels de votre boutique — calculés directement depuis vos commandes, produits et stock.',
  'merchant.analytics.loadError': 'Impossible de charger les analytics',
  'merchant.analytics.revenue': "Chiffre d'affaires",
  'merchant.analytics.revenueHint': 'Sur {{count}} commande(s) non annulée(s)',
  'merchant.analytics.avgOrderValue': 'Panier moyen',
  'merchant.analytics.avgOrderValueEmptyHint': 'Aucune commande non annulée à ce jour.',
  'merchant.analytics.cancellationRate': "Taux d'annulation",
  'merchant.analytics.cancellationRateNoOrders': 'Aucune commande enregistrée.',
  'merchant.analytics.cancellationRateNoneCancelled': 'Aucune commande annulée à ce jour.',
  'merchant.analytics.cancellationRateCount': '{{count}} commande(s) annulée(s)',
  'merchant.analytics.stockAvailable': 'Stock disponible',
  'merchant.activity.title': 'Activité récente',
  'merchant.activity.empty': 'Aucune activité récente',
  'merchant.activity.loadError': 'Impossible de charger l’activité récente',
  'merchant.activity.reason': 'Motif : {{reason}}',
  'merchant.activity.actor.system': 'Automatique',
  'merchant.activity.actor.customer': 'Par le client',
  'merchant.activity.actor.merchant': 'Par la boutique',
  'merchant.activity.order.created': 'Nouvelle commande n°{{number}}',
  'merchant.activity.order.changed': 'Commande n°{{number}} : {{to}}',
  'merchant.activity.stock.manual_adjustment': 'Ajustement du stock — {{item}} : {{delta}}',
  'merchant.activity.stock.confirmation': 'Stock sorti — {{item}} : {{delta}}',
  'merchant.activity.stock.release': 'Stock libéré — {{item}} : {{delta}}',
  'merchant.activity.stock.shipment': 'Expédition — {{item}} : {{delta}}',
  'merchant.activity.stock.return': 'Retour en stock — {{item}} : {{delta}}',
  'merchant.activity.stock.loss': 'Perte de stock — {{item}} : {{delta}}',
  'merchant.activity.stock.unknownItem': 'Article',
  'merchant.nav.todo': 'À traiter',
  'merchant.todo.title': 'À traiter',
  'merchant.todo.subtitle':
    'Ce qui demande votre attention maintenant, d’après vos commandes, votre stock et votre livraison.',
  'merchant.todo.allClear': 'Rien à traiter pour le moment.',
  'merchant.todo.loading': 'Chargement…',
  'merchant.todo.sectionError': 'Impossible de charger cette section.',
  'merchant.todo.section.orders': 'Commandes',
  'merchant.todo.section.stock': 'Stock',
  'merchant.todo.section.delivery': 'Livraison',
  'merchant.todo.section.jibex': 'Compte Jibex',
  'merchant.todo.severity.urgent': 'Urgent',
  'merchant.todo.severity.watch': 'À surveiller',
  'merchant.todo.severity.info': 'Information',
  'merchant.todo.forbidden': 'Cette section n’est pas disponible pour votre rôle.',
  'merchant.todo.newShop.title': 'Votre boutique est prête à démarrer',
  'merchant.todo.newShop.description':
    'Rien à traiter pour l’instant : ajoutez votre premier produit et complétez votre boutique pour recevoir des commandes.',
  'merchant.todo.newShop.addProduct': 'Ajouter un produit',
  'merchant.todo.newShop.completeShop': 'Compléter ma boutique',
  'merchant.todo.orders.toPrepare': '{{count}} commande(s) à préparer',
  'merchant.todo.orders.toPrepareDescription':
    'Confirmées, elles attendent que vous commenciez la préparation.',
  'merchant.todo.orders.toPack': '{{count}} commande(s) à emballer',
  'merchant.todo.orders.toPackDescription':
    'En préparation, elles ne sont pas encore marquées comme emballées.',
  'merchant.todo.stock.out': '{{count}} article(s) en rupture',
  'merchant.todo.stock.outDescription': 'Ils ne peuvent plus être commandés.',
  'merchant.todo.stock.low': '{{count}} article(s) à stock faible',
  'merchant.todo.stock.lowDescription': 'Leur stock est sous le seuil d’alerte.',
  'merchant.todo.delivery.toShip': '{{count}} commande(s) emballée(s) en attente d’expédition',
  'merchant.todo.delivery.toShipDescription': 'Aucun colis n’est encore parti pour ces commandes.',
  'merchant.todo.delivery.failed': '{{count}} envoi(s) en échec',
  'merchant.todo.delivery.failedDescription':
    'L’envoi au transporteur n’a pas abouti : ouvrez la page Livraison.',
  'merchant.todo.jibex.platformNotReady': 'Livraison Jibex indisponible',
  'merchant.todo.jibex.platformNotReadyDescription':
    'Le service n’est pas encore configuré sur la plateforme.',
  'merchant.todo.jibex.not_configured': 'Compte Jibex non connecté',
  'merchant.todo.jibex.not_configuredDescription': 'Sans compte, aucun colis ne peut être envoyé.',
  'merchant.todo.jibex.untested': 'Compte Jibex à tester',
  'merchant.todo.jibex.untestedDescription':
    'Les identifiants sont enregistrés mais n’ont pas encore été vérifiés.',
  'merchant.todo.jibex.invalid': 'Identifiants Jibex refusés',
  'merchant.todo.jibex.invalidDescription': 'Jibex a refusé les identifiants : mettez-les à jour.',
  'merchant.todo.jibex.misconfigured': 'Compte Jibex mal configuré',
  'merchant.todo.jibex.misconfiguredDescription':
    'La connexion aboutit mais le compte n’est pas utilisable (droits ou boutique).',
  'merchant.todo.jibex.network_error': 'Jibex injoignable',
  'merchant.todo.jibex.network_errorDescription':
    'Le dernier test n’a pas pu joindre Jibex : réessayez.',
  'merchant.analytics.stockReserved': 'Stock réservé',
  'merchant.analytics.topProductsTitle': 'Produits les plus commandés',
  'merchant.analytics.topProductsEmptyTitle': 'Aucun produit commandé pour le moment',
  'merchant.analytics.topProductsEmptyDescription':
    'Le classement apparaîtra ici dès la première commande non annulée.',
  'merchant.analytics.table.colQuantitySold': 'Quantité vendue',

  // ---- merchant: promotions ----
  'merchant.promotions.subtitle':
    'Créez et gérez les coupons de réduction applicables au panier entier.',
  'merchant.promotions.ownerOnlyDescription':
    'Seul le propriétaire de la boutique peut gérer les coupons.',
  'merchant.promotions.loadError': 'Impossible de charger les coupons',
  'merchant.promotions.emptyTitle': 'Aucun coupon pour le moment',
  'merchant.promotions.emptyDescription':
    'Créez votre premier coupon pour offrir une remise sur le panier entier.',
  'merchant.promotions.editTitle': 'Modifier le coupon',
  'merchant.promotions.updated': 'Coupon mis à jour.',
  'merchant.promotions.created': 'Coupon créé.',
  'merchant.promotions.deactivated': 'Coupon désactivé.',
  'merchant.promotions.reactivated': 'Coupon réactivé.',
  'merchant.promotions.table.colUsage': 'Utilisation',
  'merchant.promotions.table.colValidityWindow': 'Fenêtre de validité',
  'merchant.promotions.statusInactive': 'Désactivé',
  'merchant.promotions.historyAction': 'Historique',
  'merchant.promotions.deactivateAction': 'Désactiver',
  'merchant.promotions.noDateLimit': 'Sans limite de date',
  'merchant.promotions.form.typeLabel': 'Type',
  'merchant.promotions.form.typeFixed': 'Montant fixe (TND)',
  'merchant.promotions.form.typePercentage': 'Pourcentage (%)',
  'merchant.promotions.form.valuePercentLabel': 'Valeur (%)',
  'merchant.promotions.form.valueAmountLabel': 'Valeur (TND)',
  'merchant.promotions.form.valueHint': 'De 0 à 100.',
  'merchant.promotions.form.minOrderLabel': 'Montant minimum de commande',
  'merchant.promotions.form.minOrderHint': 'Aucun minimum si laissé vide.',
  'merchant.promotions.form.usageLimitTotalLabel': "Plafond d'utilisation total",
  'merchant.promotions.form.usageLimitHint': 'Illimité si laissé vide.',
  'merchant.promotions.form.usageLimitPerCustomerLabel': 'Plafond par client',
  'merchant.promotions.form.validFromLabel': 'Valide à partir de',
  'merchant.promotions.form.validUntilLabel': "Valide jusqu'à",
  'merchant.promotions.form.activeCheckbox': 'Coupon actif',
  'merchant.promotions.usageDrawer.title': 'Historique — {{code}}',
  'merchant.promotions.usageDrawer.loadError': "Impossible de charger l'historique",
  'merchant.promotions.usageDrawer.neverUsed': "Ce coupon n'a jamais été utilisé",
  'merchant.promotions.usageDrawer.tableCaption': 'Utilisations du coupon',
  'merchant.promotions.usageDrawer.colDiscount': 'Remise',

  // errors.* — see the header comment above and ../errors.ts. Generic,
  // never a specific business message; shown whenever a request fails in
  // one of these recognised shapes, regardless of which screen or app.
  'errors.network':
    'Impossible de contacter le serveur. Vérifiez votre connexion internet et réessayez.',
  'errors.server':
    'Une erreur est survenue de notre côté. Merci de réessayer dans quelques instants.',
  'errors.sessionExpired': 'Votre session a expiré. Veuillez vous reconnecter.',
  'errors.forbidden': "Vous n'avez pas accès à cette ressource.",
  'errors.notFound': "Cette ressource n'existe pas ou n'est plus disponible.",
  'errors.validation':
    'Le formulaire contient des champs invalides. Merci de vérifier vos informations.',
  'errors.conflict': 'Cette action entre en conflit avec une donnée existante.',
  'errors.rateLimited': 'Trop de tentatives. Merci de réessayer dans quelques instants.',
  'errors.passwordChangeRequired': "Vous devez d'abord choisir un nouveau mot de passe.",
  'errors.currentPasswordInvalid': 'Le mot de passe actuel est incorrect.',
  'errors.passwordUnchanged': 'Choisissez un mot de passe différent du mot de passe actuel.',
  'errors.passwordTooShort': 'Le nouveau mot de passe est trop court (12 caractères au moins).',
  'errors.staffAccountUnavailable':
    'Ce compte ne peut pas être créé avec cette adresse. Essayez une autre adresse.',
  'errors.staffLimitReached': 'Cette boutique a atteint la taille maximale de son équipe.',
  'errors.shopNotActive':
    "La boutique doit être activée avant de pouvoir créer des comptes d'équipe.",
  // Meta Pixel, social links, view shop, manual orders & sales (migration 0096).
  'merchant.store.form.social': 'Réseaux sociaux',
  'merchant.store.form.socialHint':
    'Collez l’adresse de vos pages. Seuls les réseaux renseignés s’affichent en haut de votre boutique.',
  'merchant.store.form.socialWhatsappNote':
    'WhatsApp se règle dans la section « Coordonnées » : il s’affiche aussi avec vos réseaux.',
  'merchant.store.form.socialFacebook': 'Page Facebook',
  'merchant.store.form.socialInstagram': 'Compte Instagram',
  'merchant.store.form.socialTiktok': 'Compte TikTok',
  'merchant.store.form.socialYoutube': 'Chaîne YouTube',
  'merchant.store.form.socialX': 'Compte X (Twitter)',
  'merchant.store.form.socialLinkedin': 'Page LinkedIn',
  'merchant.store.form.metaPixel': 'Meta Pixel (Facebook & Instagram)',
  'merchant.store.form.metaPixelLabel': 'Pixel ID',
  'merchant.store.form.metaPixelHint':
    'Le numéro de votre pixel, dans le Gestionnaire d’événements Meta (Sources de données). Laissez vide pour ne rien suivre.',
  'merchant.store.form.metaPixelPrivacy':
    'Le pixel ne se charge qu’après l’accord du visiteur (bandeau cookies). Il suit les vues, l’ajout au panier, le début de commande et les commandes passées.',
  'merchant.viewShop.label': 'Voir la boutique',
  'merchant.viewShop.short': 'Boutique',
  'merchant.viewShop.notPublic': 'Votre boutique n’est pas encore visible en ligne.',
  'merchant.viewShop.noStore': 'Créez d’abord votre boutique : nom et adresse publique.',
  'merchant.nav.sales': 'Ventes',
  'merchant.orders.add': 'Ajouter une commande',
  'merchant.orders.sourceManual': 'Saisie manuelle',
  'merchant.orders.sourceSale': 'Vente manuelle',
  'merchant.sales.title': 'Ventes',
  'merchant.sales.subtitle':
    'Les ventes enregistrées à la main (boutique physique, marché, téléphone…). Elles comptent dans le chiffre d’affaires comme les commandes en ligne, une seule fois.',
  'merchant.sales.add': 'Ajouter une vente',
  'merchant.sales.count': 'Ventes enregistrées',
  'merchant.sales.paidAmount': 'Encaissé (cette page)',
  'merchant.sales.toCollect': 'À encaisser (cette page)',
  'merchant.sales.loadError': 'Impossible de charger les ventes.',
  'merchant.sales.empty': 'Aucune vente enregistrée',
  'merchant.sales.emptyHint':
    'Enregistrez ici une vente faite hors de la boutique en ligne : le stock et le chiffre d’affaires se mettent à jour.',
  'merchant.sales.colPayment': 'Paiement',
  'merchant.sales.colDate': 'Date de vente',
  'merchant.sales.previous': 'Précédent',
  'merchant.sales.next': 'Suivant',
  'merchant.manual.newOrderTitle': 'Ajouter une commande',
  'merchant.manual.newOrderSubtitle':
    'Une commande reçue par téléphone, message ou sur place. Elle suit le parcours habituel : préparation, colis, livraison.',
  'merchant.manual.newSaleTitle': 'Ajouter une vente',
  'merchant.manual.newSaleSubtitle':
    'Une vente déjà remise au client. Le stock est mis à jour et une facture est créée.',
  'merchant.manual.backToOrders': '← Commandes',
  'merchant.manual.backToSales': '← Ventes',
  'merchant.manual.customer': 'Client',
  'merchant.manual.existingCustomer': 'Client existant',
  'merchant.manual.newCustomer': '— Nouveau client —',
  'merchant.manual.customerName': 'Nom du client',
  'merchant.manual.customerPhone': 'Téléphone',
  'merchant.manual.customerPhoneHint': 'Numéro tunisien, avec ou sans +216.',
  'merchant.manual.customerEmail': 'E-mail',
  'merchant.manual.products': 'Produits',
  'merchant.manual.loadingProducts': 'Chargement du catalogue…',
  'merchant.manual.noActiveProducts':
    'Aucun produit actif : activez au moins un produit pour enregistrer une commande.',
  'merchant.manual.product': 'Produit',
  'merchant.manual.chooseProduct': 'Choisir un produit',
  'merchant.manual.variant': 'Variante',
  'merchant.manual.chooseVariant': 'Choisir',
  'merchant.manual.quantity': 'Quantité',
  'merchant.manual.removeLine': 'Retirer',
  'merchant.manual.addLine': '+ Ajouter un produit',
  'merchant.manual.stockAvailable': '{{count}} en stock',
  'merchant.manual.stockUntracked': 'Stock non suivi',
  'merchant.manual.stockShort': 'Stock insuffisant',
  'merchant.manual.coupon': 'Code promo',
  'merchant.manual.couponHint':
    'Appliqué selon ses règles (dates, minimum, limites), vérifiées à l’enregistrement.',
  'merchant.manual.delivery': 'Livraison',
  'merchant.manual.handover': 'Remise',
  'merchant.manual.pickup': 'Remise en main propre en boutique (pas de livraison)',
  'merchant.manual.governorate': 'Gouvernorat',
  'merchant.manual.shopGovernorate': 'Gouvernorat de la boutique',
  'merchant.manual.chooseGovernorate': 'Choisir…',
  'merchant.manual.delegation': 'Délégation',
  'merchant.manual.address': 'Adresse',
  'merchant.manual.additionalInfo': 'Complément d’adresse',
  'merchant.manual.applyDeliveryFee': 'Appliquer les frais de livraison de la boutique ({{fee}})',
  'merchant.manual.paymentAndStatus': 'Paiement et statut',
  'merchant.manual.status': 'Statut de la commande',
  'merchant.manual.status.toProcess': 'À traiter',
  'merchant.manual.status.toProcessHint':
    'Elle apparaît dans « À faire », comme une commande en ligne.',
  'merchant.manual.status.processing': 'En préparation',
  'merchant.manual.status.processingHint':
    'Vous l’avez déjà commencée : prochaine étape, le colis.',
  'merchant.manual.paymentMethod': 'Mode de paiement',
  'merchant.manual.paymentStatus': 'Statut du paiement',
  'merchant.manual.paymentPaid': 'Payée',
  'merchant.manual.paymentPending': 'À encaisser',
  'merchant.manual.orderPaymentPending':
    'Paiement à encaisser : une commande manuelle n’est jamais enregistrée comme payée.',
  'merchant.manual.paymentReference': 'Référence du paiement',
  'merchant.manual.paymentReferenceHint':
    'N° de virement, de chèque ou de ticket de carte. Obligatoire pour une vente payée autrement qu’en espèces.',
  'merchant.manual.saleDate': 'Date de la vente',
  'merchant.manual.saleDateHint': 'Au plus 30 jours en arrière, jamais dans le futur.',
  'merchant.manual.internalNote': 'Note interne',
  'merchant.manual.internalNoteHint': 'Visible par votre équipe seulement, jamais par le client.',
  'merchant.manual.subtotal': 'Sous-total',
  'merchant.manual.deliveryFee': 'Livraison',
  'merchant.manual.discount': 'Réduction',
  'merchant.manual.discountOnSave': 'calculée à l’enregistrement',
  'merchant.manual.estimatedTotal': 'Total estimé',
  'merchant.manual.serverRecomputes':
    'Les prix, la réduction, les frais et le total sont recalculés par le serveur à partir du catalogue ; le stock est vérifié au même moment.',
  'merchant.manual.saveOrder': 'Enregistrer la commande',
  'merchant.manual.saveSale': 'Enregistrer la vente',
  'merchant.manual.saving': 'Enregistrement…',
  'merchant.manual.orderCreated': 'Commande n° {{number}} créée — {{total}}.',
  'merchant.manual.saleCreated': 'Vente n° {{number}} enregistrée — {{total}}.',
  'merchant.manual.payment.cod': 'Paiement à la livraison',
  'merchant.manual.payment.cash': 'Espèces',
  'merchant.manual.payment.bankTransfer': 'Virement bancaire',
  'merchant.manual.payment.card': 'Carte bancaire',
  'merchant.manual.payment.cheque': 'Chèque',
  'merchant.manual.payment.other': 'Autre',
  'merchant.manual.errors.fixForm': 'Vérifiez les champs signalés.',
  'merchant.manual.errors.nameRequired': 'Le nom du client est requis.',
  'merchant.manual.errors.phoneInvalid': 'Numéro de téléphone tunisien invalide.',
  'merchant.manual.errors.emailInvalid': 'Adresse e-mail invalide.',
  'merchant.manual.errors.productRequired': 'Choisissez un produit.',
  'merchant.manual.errors.variantRequired': 'Choisissez une variante.',
  'merchant.manual.errors.quantityInvalid': 'Quantité entre 1 et 999.',
  'merchant.manual.errors.duplicateLine':
    'Ce produit est déjà dans la liste : changez sa quantité.',
  'merchant.manual.errors.governorateRequired': 'Choisissez un gouvernorat.',
  'merchant.manual.errors.delegationRequired': 'La délégation est requise.',
  'merchant.manual.errors.addressRequired': 'L’adresse est requise.',
  'merchant.manual.errors.referenceRequired': 'Indiquez la référence du paiement.',
  'merchant.manual.errors.saleDate':
    'Date de vente : au plus 30 jours en arrière, pas dans le futur.',
  'errors.saleDateOutOfRange':
    'La date de vente doit être dans les 30 derniers jours, et pas dans le futur.',
  'errors.storeNotConfigured': 'Configurez d’abord votre boutique (menu Boutique).',
  'errors.requestInProgress': 'Cet enregistrement est déjà en cours. Patientez quelques secondes.',
  'storefront.social.label': 'Suivez-nous',
  'storefront.social.open': '{{network}} (nouvel onglet)',
  'storefront.consent.title': 'Cookies publicitaires',
  'storefront.consent.text':
    'Cette boutique utilise le pixel Meta (Facebook, Instagram) pour mesurer ses publicités, uniquement si vous l’acceptez. Les cookies nécessaires au panier restent actifs.',
  'storefront.consent.accept': 'Accepter',
  'storefront.consent.decline': 'Refuser',
  'storefront.consent.manage': 'Cookies',
  // Order attribution — « Ventes par réseau » (migration 0097).
  'merchant.attribution.title': 'Ventes par réseau',
  'merchant.attribution.subtitle':
    'D’où viennent vos commandes : applications de réseaux sociaux, publicités, recherche ou commandes saisies à la main.',
  'merchant.attribution.empty': 'Pas encore de commande à analyser.',
  'merchant.attribution.orders': '{{count}} commande{{plural}}',
  'merchant.attribution.average': 'Panier moyen : {{amount}}',
  'merchant.attribution.howItWorks':
    'Le réseau retenu est celui de la dernière visite venue d’un réseau ou d’une publicité (une visite directe ne l’efface pas) : lien avec paramètres utm (les liens de « Partager ma boutique » en ont), publicité Meta / TikTok / Google, ou navigateur intégré de l’application (Instagram, Facebook, TikTok…). « Non renseigné » : commandes plus anciennes ou origine impossible à connaître. Aucune donnée personnelle n’est collectée.',
  'merchant.attribution.via': 'via {{source}}',
  'merchant.attribution.email': 'E-mail',
  'merchant.attribution.referral': 'Autre site',
  'merchant.attribution.other': 'Autre',
  'merchant.attribution.direct': 'Accès direct',
  'merchant.attribution.phone': 'Téléphone',
  'merchant.attribution.inStore': 'En boutique',
  'merchant.attribution.manual': 'Saisie manuelle (canal non précisé)',
  'merchant.attribution.unknown': 'Non renseigné',
  'merchant.attribution.channel': 'Reçue via',
  'merchant.attribution.channelHint':
    'Le réseau par lequel le client vous a contacté : comptée dans « Ventes par réseau ».',
  'merchant.attribution.channelNone': 'Non précisé',
  'merchant.attribution.bioLinks': 'Liens à mettre sur vos réseaux',
  'merchant.attribution.bioLinksHint':
    'Chaque lien est marqué pour son réseau : les commandes passées après un clic s’afficheront « via Instagram », « via TikTok »…',
  'merchant.attribution.copyFor': 'Copier pour {{source}}',
  // Real-time dashboard (migration 0098).
  'merchant.live.on': 'En direct',
  'merchant.live.connecting': 'Connexion…',
  'merchant.live.off': 'Hors ligne',
  'merchant.live.hint':
    'Commandes et colis se mettent à jour tout seuls, sans « Actualiser ». Hors ligne : rafraîchissement toutes les 30 secondes.',
} as const;

export type MessageKey = keyof typeof fr;
